// This file is auto-generated by @hey-api/openapi-ts

import * as z from "zod";

/**
 * The kind of resource a live update refers to. A plural name refers to a list
 * belonging to a parent, whose id the topic carries.
 *
 */
export const LiveUpdateTopicNameSchema = z.enum([
    "request",
    "member-requests",
    "request-bookings",
    "booking",
]);

export type LiveUpdateTopicName = z.infer<typeof LiveUpdateTopicNameSchema>;

/**
 * Names what changed. Carries no payload; refetch the resource to read it.
 *
 */
export const LiveUpdateSchema = z.object({
    name: LiveUpdateTopicNameSchema,
    id: z.string().min(1),
});

export type LiveUpdate = z.infer<typeof LiveUpdateSchema>;

export const CreatePublicShareLinkRequestSchema = z.object({
    resourceType: z.enum(["booking", "proposal"]),
    resourceId: z.string().min(1),
});

export type CreatePublicShareLinkRequest = z.infer<
    typeof CreatePublicShareLinkRequestSchema
>;

export const PublicShareLinkDataSchema = z.object({
    shareId: z.string(),
    url: z.string(),
    expiresAt: z.iso.datetime(),
});

export type PublicShareLinkData = z.infer<typeof PublicShareLinkDataSchema>;

export const SuccessResponseCreatePublicShareLinkSchema = z.object({
    success: z.literal(true),
    data: PublicShareLinkDataSchema,
});

export type SuccessResponseCreatePublicShareLink = z.infer<
    typeof SuccessResponseCreatePublicShareLinkSchema
>;

export const CreateAgentFirebaseTokenResponseDataSchema = z.object({
    firebaseToken: z.string().min(1),
});

export type CreateAgentFirebaseTokenResponseData = z.infer<
    typeof CreateAgentFirebaseTokenResponseDataSchema
>;

export const SuccessResponseCreateAgentFirebaseTokenSchema = z.object({
    success: z.literal(true),
    data: CreateAgentFirebaseTokenResponseDataSchema,
});

export type SuccessResponseCreateAgentFirebaseToken = z.infer<
    typeof SuccessResponseCreateAgentFirebaseTokenSchema
>;

/**
 * File attachment on a note or message.
 */
export const NoteAttachmentSchema = z.object({
    id: z.string().optional(),
    name: z.string().optional(),
    filename: z.string().optional(),
    url: z.url(),
    mimeType: z.string().optional(),
    width: z.int().nullish(),
    height: z.int().nullish(),
    storageFullPath: z.string().optional(),
    storageName: z.string().optional(),
});

export type NoteAttachment = z.infer<typeof NoteAttachmentSchema>;

/**
 * Agent-authored internal note. Sender identity (`sentBy`, `senderName`, `senderFirstName`,
 * `sentByAgent`), member (`member`, `memberName`), and `requestTitle` are set server-side.
 * Catalog items and bookings are resolved server-side from IDs.
 *
 */
export const CreateInternalNoteRequestSchema = z.object({
    message: z.string(),
    messageHtml: z.string().nullish(),
    attachments: z.array(NoteAttachmentSchema).optional(),
    requestId: z.string().nullish(),
    catalogItemIds: z.array(z.string().min(1)).optional(),
    bookingIds: z.array(z.string().min(1)).optional(),
    mentions: z.array(z.string().min(1)).optional(),
    mentionDueDate: z.iso.datetime().nullish(),
});

export type CreateInternalNoteRequest = z.infer<typeof CreateInternalNoteRequestSchema>;

export const CreateInternalNoteResponseDataSchema = z.object({
    noteId: z.string().min(1),
});

export type CreateInternalNoteResponseData = z.infer<
    typeof CreateInternalNoteResponseDataSchema
>;

export const SuccessResponseCreateInternalNoteSchema = z.object({
    success: z.literal(true),
    data: CreateInternalNoteResponseDataSchema,
});

export type SuccessResponseCreateInternalNote = z.infer<
    typeof SuccessResponseCreateInternalNoteSchema
>;

export const DeleteConversationMessageResponseDataSchema = z.object({
    messageId: z.string().min(1),
});

export type DeleteConversationMessageResponseData = z.infer<
    typeof DeleteConversationMessageResponseDataSchema
>;

export const SuccessResponseDeleteConversationMessageSchema = z.object({
    success: z.literal(true),
    data: DeleteConversationMessageResponseDataSchema,
});

export type SuccessResponseDeleteConversationMessage = z.infer<
    typeof SuccessResponseDeleteConversationMessageSchema
>;

/**
 * Agent edit payload for a conversation message. Booking previews are resolved server-side
 * from `bookingIds` when provided; pass `null` to clear suggestions.
 *
 */
export const EditConversationMessageRequestSchema = z.object({
    message: z.string(),
    messageHtml: z.string().nullish(),
    bookingIds: z.array(z.string().min(1)).nullish(),
});

export type EditConversationMessageRequest = z.infer<
    typeof EditConversationMessageRequestSchema
>;

export const EditConversationMessageResponseDataSchema = z.object({
    messageId: z.string().min(1),
});

export type EditConversationMessageResponseData = z.infer<
    typeof EditConversationMessageResponseDataSchema
>;

export const SuccessResponseEditConversationMessageSchema = z.object({
    success: z.literal(true),
    data: EditConversationMessageResponseDataSchema,
});

export type SuccessResponseEditConversationMessage = z.infer<
    typeof SuccessResponseEditConversationMessageSchema
>;

/**
 * Empty JSON object. Replacement text is resolved server-side from the member's
 * service language.
 *
 */
export const SanitizeMessageRequestSchema = z.record(z.string(), z.never());

export type SanitizeMessageRequest = z.infer<typeof SanitizeMessageRequestSchema>;

/**
 * Assigns messages and internal notes in a conversation to a request.
 * At least one of `messageIds` or `noteIds` must be non-empty. Request title,
 * conversation member, and unread state are resolved server-side.
 *
 */
export const AssignConversationItemsToRequestRequestSchema = z.object({
    requestId: z.string().min(1).nullable(),
    messageIds: z.array(z.string().min(1)).optional(),
    noteIds: z.array(z.string().min(1)).optional(),
});

export type AssignConversationItemsToRequestRequest = z.infer<
    typeof AssignConversationItemsToRequestRequestSchema
>;

export const ResendConversationMessageNotificationRequestSchema = z.object({
    notificationMethod: z.enum(["push-notification", "email"]),
});

export type ResendConversationMessageNotificationRequest = z.infer<
    typeof ResendConversationMessageNotificationRequestSchema
>;

/**
 * Content fields that can be updated on an internal note.
 */
export const EditInternalNoteContentSchema = z.object({
    message: z.string().optional(),
    messageHtml: z.string().nullish(),
    attachments: z.array(NoteAttachmentSchema).optional(),
    catalogItemIds: z.array(z.string().min(1)).optional(),
    bookingIds: z.array(z.string().min(1)).optional(),
});

export type EditInternalNoteContent = z.infer<typeof EditInternalNoteContentSchema>;

/**
 * Partial update for an existing internal note. All fields are optional.
 * - `note` updates the note content (message, messageHtml, attachments, etc.)
 * - `mentions` replaces the mentioned agents; empty array removes mentions when
 * the note already had mentions (no-op when it never had mentions)
 * - `mentionDueDate` sets or clears the due date (null to clear)
 * Use `/complete` and `/reopen` to change mention task status.
 *
 */
export const EditInternalNoteRequestSchema = z.object({
    note: EditInternalNoteContentSchema.optional(),
    mentions: z.array(z.string().min(1)).optional(),
    mentionDueDate: z.iso.datetime().nullish(),
});

export type EditInternalNoteRequest = z.infer<typeof EditInternalNoteRequestSchema>;

export const EditInternalNoteResponseDataSchema = z.object({
    noteId: z.string().min(1),
});

export type EditInternalNoteResponseData = z.infer<
    typeof EditInternalNoteResponseDataSchema
>;

export const SuccessResponseEditInternalNoteSchema = z.object({
    success: z.literal(true),
    data: EditInternalNoteResponseDataSchema,
});

export type SuccessResponseEditInternalNote = z.infer<
    typeof SuccessResponseEditInternalNoteSchema
>;

/**
 * Request body for archiving a member. `archiveReason` is persisted on `memberData`.
 *
 */
export const ArchiveMemberRequestSchema = z.object({
    archiveReason: z.enum([
        "inactive",
        "trial_ended",
        "subscription_canceled",
        "android_user",
        "payment_overdue",
        "member_misconduct",
        "other",
    ]),
});

export type ArchiveMemberRequest = z.infer<typeof ArchiveMemberRequestSchema>;

/**
 * Identifier of the archived member (same as path `memberId`).
 */
export const MemberArchiveAcceptedSchema = z.object({
    id: z.string(),
});

export type MemberArchiveAccepted = z.infer<typeof MemberArchiveAcceptedSchema>;

export const SuccessResponseMemberArchiveSchema = z.object({
    success: z.literal(true),
    data: MemberArchiveAcceptedSchema,
});

export type SuccessResponseMemberArchive = z.infer<
    typeof SuccessResponseMemberArchiveSchema
>;

/**
 * Empty JSON object. Activation is keyed only by the path `memberId`.
 *
 */
export const ActivateMemberRequestSchema = z.record(z.string(), z.never());

export type ActivateMemberRequest = z.infer<typeof ActivateMemberRequestSchema>;

/**
 * Identifier of the reactivated member (same as path `memberId`).
 */
export const MemberActivationAcceptedSchema = z.object({
    id: z.string(),
});

export type MemberActivationAccepted = z.infer<typeof MemberActivationAcceptedSchema>;

export const SuccessResponseMemberActivationSchema = z.object({
    success: z.literal(true),
    data: MemberActivationAcceptedSchema,
});

export type SuccessResponseMemberActivation = z.infer<
    typeof SuccessResponseMemberActivationSchema
>;

export const SetMemberOnboardingPhoneCompletionStateRequestSchema = z.object({
    enable: z.boolean(),
});

export type SetMemberOnboardingPhoneCompletionStateRequest = z.infer<
    typeof SetMemberOnboardingPhoneCompletionStateRequestSchema
>;

export const MemberOnboardingPhoneCompletionStateSchema = z.object({
    completedByPhone: z.boolean(),
});

export type MemberOnboardingPhoneCompletionState = z.infer<
    typeof MemberOnboardingPhoneCompletionStateSchema
>;

export const SuccessResponseMemberOnboardingPhoneCompletionStateSchema = z.object({
    success: z.literal(true),
    data: MemberOnboardingPhoneCompletionStateSchema,
});

export type SuccessResponseMemberOnboardingPhoneCompletionState = z.infer<
    typeof SuccessResponseMemberOnboardingPhoneCompletionStateSchema
>;

export const ManageMemberRequestSchema = z.object({
    assignedAgentId: z.string().optional(),
    groupIds: z.array(z.string()).optional(),
    serviceLanguage: z.enum(["en", "de", "fr"]).optional(),
    source: z
        .enum([
            "albertine-employee",
            "introducer",
            "investor-pool",
            "partnership",
            "member-referral",
            "event",
            "shareholder-referral",
            "other",
        ])
        .optional(),
    additionalSourceInfo: z.string().optional(),
});

export type ManageMemberRequest = z.infer<typeof ManageMemberRequestSchema>;

export const CreateMemberResponseDataSchema = z.object({
    id: z.string(),
});

export type CreateMemberResponseData = z.infer<typeof CreateMemberResponseDataSchema>;

export const SuccessResponseCreateMemberSchema = z.object({
    success: z.literal(true),
    data: CreateMemberResponseDataSchema,
});

export type SuccessResponseCreateMember = z.infer<
    typeof SuccessResponseCreateMemberSchema
>;

export const ValidateNewMemberRequestSchema = z.object({
    email: z
        .email()
        .regex(/^[^A-Z]*$/)
        .optional(),
    phoneNumber: z.string().min(1).optional(),
});

export type ValidateNewMemberRequest = z.infer<typeof ValidateNewMemberRequestSchema>;

export const ValidateNewMemberResponseDataSchema = z.object({
    emailInUse: z.boolean(),
    phoneNumberInUse: z.boolean(),
});

export type ValidateNewMemberResponseData = z.infer<
    typeof ValidateNewMemberResponseDataSchema
>;

export const SuccessResponseValidateNewMemberSchema = z.object({
    success: z.literal(true),
    data: ValidateNewMemberResponseDataSchema,
});

export type SuccessResponseValidateNewMember = z.infer<
    typeof SuccessResponseValidateNewMemberSchema
>;

/**
 * ISO 8601 calendar date only (yyyy-MM-dd)
 */
export const IsoDateSchema = z
    .string()
    .regex(/^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/);

export type IsoDate = z.infer<typeof IsoDateSchema>;

/**
 * City/country for member preferences (matches ThemeLocation shape in storage).
 */
export const TopCitySchema = z.object({
    city: z.string(),
    country: z.string(),
    countryCode: z.string().max(2),
});

export type TopCity = z.infer<typeof TopCitySchema>;

/**
 * Full replacement of member personal information.
 * - `firstName` and `lastName` are required (may be empty strings).
 * - Omitted optional fields are **deleted** from the member document.
 * - `dateOfBirth` must be ISO date-only `yyyy-MM-dd` when present.
 *
 */
export const EditMemberPersonalInfoRequestSchema = z.object({
    title: z.string().optional(),
    firstName: z.string(),
    lastName: z.string(),
    greetingsName: z.string().nullish(),
    familyMustKnow: z.string().optional(),
    personalMustKnow: z.string().optional(),
    personalHighlight: z.string().optional(),
    interests: z.string().optional(),
    loves: z.string().optional(),
    hates: z.string().optional(),
    dateOfBirth: IsoDateSchema.optional(),
    occupation: z.string().optional(),
    memberships: z.string().optional(),
    topCities: z.array(TopCitySchema).optional(),
});

export type EditMemberPersonalInfoRequest = z.infer<
    typeof EditMemberPersonalInfoRequestSchema
>;

/**
 * Preferred communication channel to reach the member. Omitting this field deletes it from the member document.
 */
export const MemberPreferredCommunicationChannelSchema = z.enum([
    "app",
    "whatsapp",
    "email",
    "phone-call",
]);

export type MemberPreferredCommunicationChannel = z.infer<
    typeof MemberPreferredCommunicationChannelSchema
>;

/**
 * Card eligibility check details. Omitting this field deletes it from the member document.
 */
export const MemberEligibilityDetailsSchema = z.object({
    bin: z.string().optional(),
    last4: z.string().optional(),
});

export type MemberEligibilityDetails = z.infer<typeof MemberEligibilityDetailsSchema>;

/**
 * Full replacement of member contact details.
 * - `email` and `phoneNumber` can be omitted only if not already set.
 * - Changing an already-set `email` or `phoneNumber` to a different value is allowed.
 * - Adding new `email` or `phoneNumber` (when not set) requires admin scope.
 * - `email` and `phoneNumber` must be unique across all members.
 * - Omitted optional fields (`secondaryPhone`, `secondaryEmail`, `preferredCommunicationChannel`, `channelContact`, `address`, `postalCode`, `city`, `state`, `country`, `timeZone`, `eligibilityDetails`) are **deleted** from the member document.
 *
 */
export const EditMemberContactDetailsRequestSchema = z.object({
    email: z.email().optional(),
    phoneNumber: z.string().optional(),
    secondaryPhone: z.string().optional(),
    secondaryEmail: z.email().optional(),
    preferredCommunicationChannel: MemberPreferredCommunicationChannelSchema.optional(),
    channelContact: z.enum(["primary", "secondary"]).optional(),
    address: z.string().optional(),
    postalCode: z.string().optional(),
    city: z.string().optional(),
    state: z.string().optional(),
    country: z.string().optional(),
    timeZone: z.string().optional(),
    eligibilityDetails: MemberEligibilityDetailsSchema.optional(),
});

export type EditMemberContactDetailsRequest = z.infer<
    typeof EditMemberContactDetailsRequestSchema
>;

/**
 * Philanthropy contribution amounts (trees, kelp biomass, and habitat).
 */
export const ContributionsSchema = z.object({
    trees: z.int().gte(0),
    kilograms: z.int().gte(0),
    squareMeters: z.int().gte(0),
});

export type Contributions = z.infer<typeof ContributionsSchema>;

export const SuccessResponseContributionTotalsSchema = z.object({
    success: z.literal(true),
    data: ContributionsSchema,
});

export type SuccessResponseContributionTotals = z.infer<
    typeof SuccessResponseContributionTotalsSchema
>;

/**
 * Update member philanthropy contributions.
 *
 */
export const EditMemberContributionsRequestSchema = z.object({
    contributions: ContributionsSchema,
});

export type EditMemberContributionsRequest = z.infer<
    typeof EditMemberContributionsRequestSchema
>;

/**
 * Update member payment details.
 * All fields are optional. Omitted fields are not updated.
 *
 */
export const EditMemberPaymentDetailsRequestSchema = z.object({
    paymentsMustKnow: z.string().optional(),
    preferredCurrency: z.string().optional(),
    stripeCustomerId: z.string().optional(),
    invoiceNameAliases: z.array(z.string().min(1).max(200)).max(10).optional(),
});

export type EditMemberPaymentDetailsRequest = z.infer<
    typeof EditMemberPaymentDetailsRequestSchema
>;

/**
 * Payment hints for LMT (preferred currency, must-know notes, invoice names).
 */
export const MemberPaymentPreferencesSchema = z.object({
    paymentsMustKnow: z.string().nullable(),
    preferredCurrency: z.string().nullable(),
    invoiceNameAliases: z.array(z.string()),
});

export type MemberPaymentPreferences = z.infer<typeof MemberPaymentPreferencesSchema>;

export const SuccessResponseMemberPaymentPreferencesSchema = z.object({
    success: z.literal(true),
    data: MemberPaymentPreferencesSchema,
});

export type SuccessResponseMemberPaymentPreferences = z.infer<
    typeof SuccessResponseMemberPaymentPreferencesSchema
>;

/**
 * A benefit the member reacted to during onboarding.
 */
export const OnboardingBenefitPreferenceSchema = z.object({
    title: z.string(),
    coverImageUrl: z.string(),
    feedback: z.enum(["neutral", "like", "heart", "dislike"]),
    description: z.string().nullable(),
    group: z.string(),
});

export type OnboardingBenefitPreference = z.infer<
    typeof OnboardingBenefitPreferenceSchema
>;

/**
 * A recommendation category the member reacted to during onboarding.
 */
export const OnboardingRecommendationPreferenceSchema = z.object({
    title: z.string(),
    coverImageUrl: z.string(),
    feedback: z.enum(["neutral", "like", "heart", "dislike"]),
    description: z.string().nullable(),
    category: z.string(),
});

export type OnboardingRecommendationPreference = z.infer<
    typeof OnboardingRecommendationPreferenceSchema
>;

/**
 * Member onboarding answers shown in the profile panel and chat compose.
 */
export const MemberOnboardingPreferencesSchema = z.object({
    greetingsName: z.string().nullable(),
    anyUpcomingTravelPlans: z.enum(["yes", "no", "some"]).nullable(),
    upcomingTravelQuery: z.string().nullable(),
    anyUpcomingDiningPlans: z.enum(["yes", "no"]).nullable(),
    upcomingDiningQuery: z.string().nullable(),
    hasUsedConcierge: z.enum(["yes", "no", "maybe"]).nullable(),
    hasUsedConciergeFor: z.string().nullable(),
    benefits: z.array(OnboardingBenefitPreferenceSchema).nullable(),
    recommendations: z.array(OnboardingRecommendationPreferenceSchema).nullable(),
    createdAt: z.iso.datetime().nullable(),
});

export type MemberOnboardingPreferences = z.infer<
    typeof MemberOnboardingPreferencesSchema
>;

export const SuccessResponseMemberOnboardingPreferencesSchema = z.object({
    success: z.literal(true),
    data: MemberOnboardingPreferencesSchema,
});

export type SuccessResponseMemberOnboardingPreferences = z.infer<
    typeof SuccessResponseMemberOnboardingPreferencesSchema
>;

/**
 * Path parameters for listing a member's latest proactivity shares.
 */
export const ListMemberProactivityPathParamsSchema = z.object({
    memberId: z.string().min(1),
});

export type ListMemberProactivityPathParams = z.infer<
    typeof ListMemberProactivityPathParamsSchema
>;

/**
 * A catalog item an agent shared with this member in chat, outside of a request.
 *
 */
export const MemberProactivityItemSchema = z.object({
    messageId: z.string().min(1),
    sharedAt: z.iso.datetime(),
});

export type MemberProactivityItem = z.infer<typeof MemberProactivityItemSchema>;

export const MemberProactivityDataSchema = z.object({
    items: z.array(MemberProactivityItemSchema),
});

export type MemberProactivityData = z.infer<typeof MemberProactivityDataSchema>;

export const SuccessResponseMemberProactivitySchema = z.object({
    success: z.literal(true),
    data: MemberProactivityDataSchema,
});

export type SuccessResponseMemberProactivity = z.infer<
    typeof SuccessResponseMemberProactivitySchema
>;

/**
 * Path parameters for listing a member's referral promotions.
 */
export const ListMemberReferralPromotionsPathParamsSchema = z.object({
    memberId: z.string().min(1),
});

export type ListMemberReferralPromotionsPathParams = z.infer<
    typeof ListMemberReferralPromotionsPathParamsSchema
>;

/**
 * Path parameters for creating a member referral promotion.
 */
export const CreateMemberReferralPromotionPathParamsSchema = z.object({
    memberId: z.string().min(1),
});

export type CreateMemberReferralPromotionPathParams = z.infer<
    typeof CreateMemberReferralPromotionPathParamsSchema
>;

/**
 * Path parameters for updating a member referral promotion.
 */
export const UpdateMemberReferralPromotionPathParamsSchema = z.object({
    memberId: z.string().min(1),
    promotionId: z.uuid(),
});

export type UpdateMemberReferralPromotionPathParams = z.infer<
    typeof UpdateMemberReferralPromotionPathParamsSchema
>;

/**
 * Copy shown to the member for a new referral promotion.
 */
export const CreateMemberReferralPromotionRequestSchema = z.object({
    popupDescription: z.string().min(1).max(260),
    notificationDescription: z.string().min(1).max(150),
});

export type CreateMemberReferralPromotionRequest = z.infer<
    typeof CreateMemberReferralPromotionRequestSchema
>;

/**
 * Updated copy for an in-progress referral promotion.
 */
export const UpdateMemberReferralPromotionRequestSchema = z.object({
    popupDescription: z.string().min(1).max(260),
    notificationDescription: z.string().min(1).max(150),
});

export type UpdateMemberReferralPromotionRequest = z.infer<
    typeof UpdateMemberReferralPromotionRequestSchema
>;

/**
 * Derived from the promotion window and whether the member has nominated
 * someone. `active` is in progress with no nomination; `successful` has a
 * nomination; `unsuccessful` ended without a nomination.
 *
 */
export const MemberReferralPromotionStatusSchema = z.enum([
    "active",
    "successful",
    "unsuccessful",
]);

export type MemberReferralPromotionStatus = z.infer<
    typeof MemberReferralPromotionStatusSchema
>;

/**
 * A member referral promotion and its current status.
 */
export const MemberReferralPromotionSchema = z.object({
    id: z.uuid(),
    status: MemberReferralPromotionStatusSchema,
    startTime: z.iso.datetime(),
    endTime: z.iso.datetime(),
    popupDescription: z.string(),
    notificationDescription: z.string(),
});

export type MemberReferralPromotion = z.infer<typeof MemberReferralPromotionSchema>;

export const MemberReferralPromotionsDataSchema = z.object({
    items: z.array(MemberReferralPromotionSchema),
});

export type MemberReferralPromotionsData = z.infer<
    typeof MemberReferralPromotionsDataSchema
>;

export const SuccessResponseMemberReferralPromotionsSchema = z.object({
    success: z.literal(true),
    data: MemberReferralPromotionsDataSchema,
});

export type SuccessResponseMemberReferralPromotions = z.infer<
    typeof SuccessResponseMemberReferralPromotionsSchema
>;

export const SuccessResponseMemberReferralPromotionSchema = z.object({
    success: z.literal(true),
    data: MemberReferralPromotionSchema,
});

export type SuccessResponseMemberReferralPromotion = z.infer<
    typeof SuccessResponseMemberReferralPromotionSchema
>;

/**
 * Stripe billing fields for LMT from the member's active `payment_provider_accounts` row
 * (Postgres only; not verified against Stripe at read time).
 *
 */
export const MemberStripeAccountSchema = z.object({
    stripeCustomerId: z.string(),
    name: z.string().nullable(),
    email: z.string().nullable(),
});

export type MemberStripeAccount = z.infer<typeof MemberStripeAccountSchema>;

export const SuccessResponseMemberStripeAccountSchema = z.object({
    success: z.literal(true),
    data: MemberStripeAccountSchema.nullable(),
});

export type SuccessResponseMemberStripeAccount = z.infer<
    typeof SuccessResponseMemberStripeAccountSchema
>;

/**
 * Request to link a member profile to another member as a family member.
 *
 */
export const LinkFamilyMemberProfileRequestSchema = z.object({
    memberIdToLink: z.string().min(1),
});

export type LinkFamilyMemberProfileRequest = z.infer<
    typeof LinkFamilyMemberProfileRequestSchema
>;

/**
 * Request to unlink a family member profile from a member.
 *
 */
export const UnlinkFamilyMemberProfileRequestSchema = z.object({
    memberIdToUnlink: z.string().min(1),
});

export type UnlinkFamilyMemberProfileRequest = z.infer<
    typeof UnlinkFamilyMemberProfileRequestSchema
>;

/**
 * Request to create a limited profile family member (non-member family member like children, spouse without an account).
 *
 */
export const LinkLimitedProfileFamilyMemberRequestSchema = z.object({
    firstName: z.string().min(1),
    lastName: z.string().min(1),
    relationship: z.string().min(3),
    mustKnow: z.string().optional(),
    dateOfBirth: IsoDateSchema.optional(),
    passportName: z.string().optional(),
    passportNumber: z.string().optional(),
    passportExpiryDate: IsoDateSchema.optional(),
    nationality: z.string().optional(),
    frequentFlyerNumber: z.string().optional(),
});

export type LinkLimitedProfileFamilyMemberRequest = z.infer<
    typeof LinkLimitedProfileFamilyMemberRequestSchema
>;

/**
 * Request to update a limited profile family member. All fields are optional - only provided fields will be updated.
 *
 */
export const EditLimitedProfileFamilyMemberRequestSchema = z.object({
    firstName: z.string().min(1).optional(),
    lastName: z.string().min(1).optional(),
    relationship: z.string().min(3).optional(),
    mustKnow: z.string().optional(),
    dateOfBirth: IsoDateSchema.optional(),
    passportName: z.string().optional(),
    passportNumber: z.string().optional(),
    passportExpiryDate: IsoDateSchema.optional(),
    nationality: z.string().optional(),
    frequentFlyerNumber: z.string().optional(),
});

export type EditLimitedProfileFamilyMemberRequest = z.infer<
    typeof EditLimitedProfileFamilyMemberRequestSchema
>;

/**
 * A limited profile family member (non-member family member)
 */
export const LimitedFamilyMemberProfileSchema = z.object({
    id: z.uuid(),
    firstName: z.string().min(1),
    lastName: z.string().min(1),
    relationship: z.string().min(3),
    mustKnow: z.string().optional(),
    dateOfBirth: z.union([IsoDateSchema, z.string().max(0)]).optional(),
    passportName: z.string().optional(),
    passportNumber: z.string().optional(),
    passportExpiryDate: z.union([IsoDateSchema, z.string().max(0)]).optional(),
    nationality: z.string().optional(),
    frequentFlyerNumber: z.string().optional(),
});

export type LimitedFamilyMemberProfile = z.infer<
    typeof LimitedFamilyMemberProfileSchema
>;

export const SuccessResponseLimitedFamilyMemberProfileSchema = z.object({
    success: z.literal(true),
    data: LimitedFamilyMemberProfileSchema,
});

export type SuccessResponseLimitedFamilyMemberProfile = z.infer<
    typeof SuccessResponseLimitedFamilyMemberProfileSchema
>;

export const SuccessResponseEmptySchema = z.object({
    success: z.literal(true),
    data: z.null(),
});

export type SuccessResponseEmpty = z.infer<typeof SuccessResponseEmptySchema>;

/**
 * A link/document associated with a member
 */
export const MemberLinkSchema = z.object({
    id: z.string(),
    url: z.string(),
    title: z.string(),
});

export type MemberLink = z.infer<typeof MemberLinkSchema>;

export const BookingReservationStatusSchema = z.enum([
    "unselected",
    "prebooked",
    "confirmed",
    "cancelled",
]);

export type BookingReservationStatus = z.infer<typeof BookingReservationStatusSchema>;

export const RequestSourceSchema = z.enum(["reactive", "proactive"]);

export type RequestSource = z.infer<typeof RequestSourceSchema>;

export const RequestAiCaptureStatusSchema = z.enum(["analysing", "complete", "failed"]);

export type RequestAiCaptureStatus = z.infer<typeof RequestAiCaptureStatusSchema>;

/**
 * A field the AI capture filled in, highlighted for agent review.
 */
export const RequestHighlightedFieldSchema = z.enum([
    "title",
    "offeringCategory",
    "location",
    "locationGooglePlaceId",
    "numberOfGuests",
    "startDate",
    "startTime",
    "endDate",
    "endTime",
    "budget",
    "firstChoice",
]);

export type RequestHighlightedField = z.infer<typeof RequestHighlightedFieldSchema>;

export const RequestProposalMessageSchema = z.object({
    message: z.string(),
    bookings: z.array(z.string()),
    sentAt: z.iso.datetime(),
});

export type RequestProposalMessage = z.infer<typeof RequestProposalMessageSchema>;

export const RequestProposalSchema = z.object({
    messages: z.array(RequestProposalMessageSchema),
});

export type RequestProposal = z.infer<typeof RequestProposalSchema>;

/**
 * Query parameters for GET /bookings. At least one of `requestId`, `memberId`, or
 * `bookingIds` is required. Archived bookings are excluded unless `archived` is
 * `true`.
 *
 */
export const ListBookingsQuerySchema = z.object({
    requestId: z.string().optional(),
    memberId: z.string().optional(),
    reservationStatus: BookingReservationStatusSchema.optional(),
    bookingIds: z
        .preprocess((val) => {
            if (!val) return undefined;
            return Array.isArray(val) ? val : [String(val)];
        }, z.array(z.string()).max(100))
        .optional(),
    archived: z.enum(["true", "false"]).optional(),
});

export type ListBookingsQuery = z.infer<typeof ListBookingsQuerySchema>;

export const CatalogItemReviewSchema = z.object({
    id: z.string(),
    memberId: z.string(),
    memberName: z.string().nullable(),
    bookingRating: z.number().nullable(),
    bookingFeedback: z.string().nullable(),
    bookingFeedbackTimestamp: z.iso.datetime().nullable(),
});

export type CatalogItemReview = z.infer<typeof CatalogItemReviewSchema>;

export const CatalogItemReviewsDataSchema = z.object({
    average: z.number().nullable(),
    ratingsCount: z.int().gte(0),
    reviews: z.array(CatalogItemReviewSchema),
});

export type CatalogItemReviewsData = z.infer<typeof CatalogItemReviewsDataSchema>;

export const SuccessResponseGetCatalogItemReviewsSchema = z.object({
    success: z.literal(true),
    data: CatalogItemReviewsDataSchema,
});

export type SuccessResponseGetCatalogItemReviews = z.infer<
    typeof SuccessResponseGetCatalogItemReviewsSchema
>;

export const ResendBookingCalendarEventRequestSchema = z.object({
    forceNotify: z.boolean(),
});

export type ResendBookingCalendarEventRequest = z.infer<
    typeof ResendBookingCalendarEventRequestSchema
>;

/**
 * Sets the booking reservation status. Use this endpoint for all status changes,
 * including cancellation.
 *
 */
export const ChangeBookingStatusRequestSchema = z.object({
    status: BookingReservationStatusSchema,
});

export type ChangeBookingStatusRequest = z.infer<
    typeof ChangeBookingStatusRequestSchema
>;

export const CreateBookingResponseDataSchema = z.object({
    bookingId: z.string(),
});

export type CreateBookingResponseData = z.infer<typeof CreateBookingResponseDataSchema>;

export const SuccessResponseCreateBookingSchema = z.object({
    success: z.literal(true),
    data: CreateBookingResponseDataSchema,
});

export type SuccessResponseCreateBooking = z.infer<
    typeof SuccessResponseCreateBookingSchema
>;

/**
 * Absolute (pixel) crop rectangle for the booking image.
 */
export const BookingPixelCropSchema = z.object({
    x: z.number(),
    y: z.number(),
    width: z.number(),
    height: z.number(),
    unit: z.enum(["px"]),
});

export type BookingPixelCrop = z.infer<typeof BookingPixelCropSchema>;

/**
 * Relative (percent) crop rectangle for the booking image.
 */
export const BookingPercentCropSchema = z.object({
    x: z.number(),
    y: z.number(),
    width: z.number(),
    height: z.number(),
    unit: z.enum(["%"]),
});

export type BookingPercentCrop = z.infer<typeof BookingPercentCropSchema>;

/**
 * Notes shared with the member for this booking.
 */
export const BookingNotesSchema = z.object({
    text: z.string().nullable(),
    senderId: z.string().nullable(),
    senderName: z.string().nullable(),
});

export type BookingNotes = z.infer<typeof BookingNotesSchema>;

export const BookingSuppliersSchema = z.object({
    ids: z.array(z.string()).nullable(),
    details: z.string().nullable(),
});

export type BookingSuppliers = z.infer<typeof BookingSuppliersSchema>;

export const BookingProposalSchema = z.object({
    feedback: z.string().nullable(),
    feedbackTimestamp: z.iso.datetime().nullable(),
});

export type BookingProposal = z.infer<typeof BookingProposalSchema>;

/**
 * Member rating and feedback for the booking.
 */
export const BookingReviewSchema = z.object({
    rating: z.number().nullable(),
    feedback: z.string().nullable(),
    feedbackTimestamp: z.iso.datetime().nullable(),
});

export type BookingReview = z.infer<typeof BookingReviewSchema>;

/**
 * Input schema for booking attachments/images.
 * For images, server-generated metadata (filename, dimensions, storage paths,
 * crops) is preserved automatically when matching an existing image by `id`/`url`.
 * For file attachments uploaded from LMT, clients may send metadata fields
 * (`filename`, `mimeType`, storage paths, dimensions); omitted fields are
 * preserved from any matching existing attachment.
 *
 */
export const BookingAttachmentInputSchema = z.object({
    id: z.string().optional(),
    url: z.url(),
    croppedImageURL: z.string().nullish(),
    isPublic: z.boolean().nullish(),
    filename: z.string().optional(),
    mimeType: z.string().optional(),
    name: z.string().optional(),
    storageFullPath: z.string().optional(),
    storageName: z.string().optional(),
    height: z.number().nullish(),
    width: z.number().nullish(),
});

export type BookingAttachmentInput = z.infer<typeof BookingAttachmentInputSchema>;

/**
 * Request to create a new link for a member.
 *
 */
export const CreateMemberLinkRequestSchema = z.object({
    url: z.url(),
    title: z.string().min(3),
});

export type CreateMemberLinkRequest = z.infer<typeof CreateMemberLinkRequestSchema>;

/**
 * Source request to copy bookings from.
 */
export const CopyRequestBookingsRequestSchema = z.object({
    fromRequestId: z.string().min(1),
});

export type CopyRequestBookingsRequest = z.infer<
    typeof CopyRequestBookingsRequestSchema
>;

/**
 * Crop region as percentages of the source image width/height.
 * The server derives the pixel crop from decoded image dimensions.
 *
 */
export const CropPercentageRegionSchema = z.object({
    x: z.number().gte(0).lte(100),
    y: z.number().gte(0).lte(100),
    width: z.number().gt(0).lte(100),
    height: z.number().gt(0).lte(100),
});

export type CropPercentageRegion = z.infer<typeof CropPercentageRegionSchema>;

/**
 * Full replacement of the booking gallery and primary image.
 *
 */
export const UpdateBookingImagesRequestSchema = z.object({
    images: z.array(BookingAttachmentInputSchema).nullable(),
    imageURL: z.string().nullable(),
    croppedImageURL: z.string().nullish(),
});

export type UpdateBookingImagesRequest = z.infer<
    typeof UpdateBookingImagesRequestSchema
>;

/**
 * Full replacement of the booking attachments.
 */
export const UpdateBookingAttachmentsRequestSchema = z.object({
    attachments: z.array(BookingAttachmentInputSchema),
});

export type UpdateBookingAttachmentsRequest = z.infer<
    typeof UpdateBookingAttachmentsRequestSchema
>;

/**
 * External image URLs to download and attach to the booking.
 */
export const FetchBookingImagesRequestSchema = z.object({
    urls: z.array(z.url()).min(1),
});

export type FetchBookingImagesRequest = z.infer<typeof FetchBookingImagesRequestSchema>;

/**
 * Identifies a booking image by ID and the percent crop to apply.
 * The source URL and pixel crop are resolved server-side.
 *
 */
export const CropBookingImageRequestSchema = z.object({
    imageId: z.string().min(1),
    crop: CropPercentageRegionSchema,
});

export type CropBookingImageRequest = z.infer<typeof CropBookingImageRequestSchema>;

/**
 * Request to cancel an active membership.
 */
export const CancelMembershipRequestSchema = z.object({
    reason: z.enum([
        "access",
        "price",
        "value",
        "service-level",
        "would-not-use",
        "other",
        "unknown",
    ]),
    additionalNote: z.string().optional(),
});

export type CancelMembershipRequest = z.infer<typeof CancelMembershipRequestSchema>;

/**
 * Request to convert a trial membership to paid.
 */
export const ConvertTrialToPaidRequestSchema = z.object({
    tier: z.string().min(1),
    commitmentPeriod: z.enum(["12-month"]),
    billingPeriod: z.enum(["monthly", "yearly"]),
    startDate: z.iso.date().optional(),
});

export type ConvertTrialToPaidRequest = z.infer<typeof ConvertTrialToPaidRequestSchema>;

/**
 * Request to extend a trial membership.
 */
export const ExtendTrialRequestSchema = z.object({
    extendByDays: z.int().gte(-3650).lte(3650),
});

export type ExtendTrialRequest = z.infer<typeof ExtendTrialRequestSchema>;

/**
 * Request to update an existing link. At least one field must be provided.
 *
 */
export const UpdateMemberLinkRequestSchema = z.object({
    url: z.url().optional(),
    title: z.string().min(3).optional(),
});

export type UpdateMemberLinkRequest = z.infer<typeof UpdateMemberLinkRequestSchema>;

export const SuccessResponseMemberLinkSchema = z.object({
    success: z.literal(true),
    data: MemberLinkSchema,
});

export type SuccessResponseMemberLink = z.infer<typeof SuccessResponseMemberLinkSchema>;

/**
 * Update member dining preferences. All fields are optional.
 * Omitted fields are **deleted** from the member document.
 *
 */
export const EditMemberDiningDetailsRequestSchema = z.object({
    diningPreferences: z.string().optional(),
    foodAllergies: z.string().optional(),
    diningFuturePlans: z.string().optional(),
});

export type EditMemberDiningDetailsRequest = z.infer<
    typeof EditMemberDiningDetailsRequestSchema
>;

/**
 * Update member travel details. All fields are optional.
 * Omitted fields are **deleted** from the member document.
 *
 */
export const EditMemberTravelDetailsRequestSchema = z.object({
    travelMustKnow: z.string().optional(),
    nationality: z.string().optional(),
    passportName: z.string().optional(),
    passportNumber: z.string().optional(),
    passportExpiryDate: z.union([IsoDateSchema, z.string().max(0)]).optional(),
    airlinePreferences: z.string().optional(),
    frequentFlyerNumber: z.string().optional(),
    travelFuturePlans: z.string().optional(),
});

export type EditMemberTravelDetailsRequest = z.infer<
    typeof EditMemberTravelDetailsRequestSchema
>;

/**
 * Update member events preferences. All fields are optional.
 * Omitted fields are **deleted** from the member document.
 *
 */
export const EditMemberEventsDetailsRequestSchema = z.object({
    eventsAndExperiences: z.string().optional(),
});

export type EditMemberEventsDetailsRequest = z.infer<
    typeof EditMemberEventsDetailsRequestSchema
>;

/**
 * Update member wellness preferences. All fields are optional.
 * Omitted fields are **deleted** from the member document.
 *
 */
export const EditMemberWellnessDetailsRequestSchema = z.object({
    wellnessMustKnow: z.string().optional(),
});

export type EditMemberWellnessDetailsRequest = z.infer<
    typeof EditMemberWellnessDetailsRequestSchema
>;

/**
 * Update member shopping preferences. All fields are optional.
 * Omitted fields are **deleted** from the member document.
 *
 */
export const EditMemberShoppingDetailsRequestSchema = z.object({
    luxuryItemsAndCollectibles: z.string().optional(),
});

export type EditMemberShoppingDetailsRequest = z.infer<
    typeof EditMemberShoppingDetailsRequestSchema
>;

/**
 * Update member family details. All fields are optional.
 * Omitted fields are **deleted** from the member document.
 *
 */
export const EditMemberFamilyDetailsRequestSchema = z.object({
    familyMustKnow: z.string().optional(),
});

export type EditMemberFamilyDetailsRequest = z.infer<
    typeof EditMemberFamilyDetailsRequestSchema
>;

/**
 * Path parameters for LMT message attachment download.
 */
export const LmtMessageAttachmentPathParamsSchema = z.object({
    memberId: z.string().min(1),
    messageId: z.string().min(1),
});

export type LmtMessageAttachmentPathParams = z.infer<
    typeof LmtMessageAttachmentPathParamsSchema
>;

/**
 * Path parameters for clearing agent unseen message reactions.
 */
export const ClearAgentConversationUnseenReactionsPathParamsSchema = z.object({
    memberId: z.string().min(1),
    conversationId: z.string().min(1),
});

export type ClearAgentConversationUnseenReactionsPathParams = z.infer<
    typeof ClearAgentConversationUnseenReactionsPathParamsSchema
>;

/**
 * Path parameters for marking agent conversation messages as read.
 */
export const MarkAgentConversationMessagesReadPathParamsSchema = z.object({
    memberId: z.string().min(1),
    conversationId: z.string().min(1),
});

export type MarkAgentConversationMessagesReadPathParams = z.infer<
    typeof MarkAgentConversationMessagesReadPathParamsSchema
>;

/**
 * Optional scope for marking messages as read.
 */
export const MarkAgentConversationMessagesReadRequestSchema = z.object({
    requestId: z.string().min(1).nullish(),
});

export type MarkAgentConversationMessagesReadRequest = z.infer<
    typeof MarkAgentConversationMessagesReadRequestSchema
>;

/**
 * Path parameters for generating a member conversation AI summary.
 */
export const GenerateMemberSummaryPathParamsSchema = z.object({
    memberId: z.string().min(1),
    conversationId: z.string().min(1),
});

export type GenerateMemberSummaryPathParams = z.infer<
    typeof GenerateMemberSummaryPathParamsSchema
>;

/**
 * Path parameters for sending a message to a member's general chat.
 */
export const SendGeneralChatMessageToMemberPathParamsSchema = z.object({
    memberId: z.string().min(1),
});

export type SendGeneralChatMessageToMemberPathParams = z.infer<
    typeof SendGeneralChatMessageToMemberPathParamsSchema
>;

/**
 * Path parameters for sending a message to a member's request chat.
 */
export const SendRequestChatMessageToMemberPathParamsSchema = z.object({
    memberId: z.string().min(1),
    requestId: z.string().min(1),
});

export type SendRequestChatMessageToMemberPathParams = z.infer<
    typeof SendRequestChatMessageToMemberPathParamsSchema
>;

/**
 * Text content of a chat message in a single language.
 */
export const MessageTextContentSchema = z.object({
    messageText: z.string(),
    messageHtml: z.string().nullable(),
    language: z.string().min(1),
});

export type MessageTextContent = z.infer<typeof MessageTextContentSchema>;

export const SendChatMessageToMemberContentSchema = z.object({
    originalMessage: MessageTextContentSchema,
    translations: z.array(MessageTextContentSchema).optional(),
});

export type SendChatMessageToMemberContent = z.infer<
    typeof SendChatMessageToMemberContentSchema
>;

/**
 * Path parameters for translating a chat message for a member.
 */
export const TranslateChatMessagePathParamsSchema = z.object({
    memberId: z.string().min(1),
});

export type TranslateChatMessagePathParams = z.infer<
    typeof TranslateChatMessagePathParamsSchema
>;

export const SuccessResponseTranslateChatMessageSchema = z.object({
    success: z.literal(true),
    data: MessageTextContentSchema,
});

export type SuccessResponseTranslateChatMessage = z.infer<
    typeof SuccessResponseTranslateChatMessageSchema
>;

export const SendChatMessageToMemberCatalogItemAttachmentSchema = z.object({
    type: z.literal("catalog-item"),
    id: z.string().min(1),
});

export type SendChatMessageToMemberCatalogItemAttachment = z.infer<
    typeof SendChatMessageToMemberCatalogItemAttachmentSchema
>;

export const SendChatMessageToMemberFileAttachmentSchema = z.object({
    type: z.literal("file"),
    fileUploadId: z.string().min(1),
});

export type SendChatMessageToMemberFileAttachment = z.infer<
    typeof SendChatMessageToMemberFileAttachmentSchema
>;

export const SendChatMessageToMemberBookingSuggestionAttachmentSchema = z.object({
    type: z.literal("booking-suggestion"),
    bookingId: z.string().min(1),
});

export type SendChatMessageToMemberBookingSuggestionAttachment = z.infer<
    typeof SendChatMessageToMemberBookingSuggestionAttachmentSchema
>;

export const SendChatMessageToMemberAttachmentSchema = z.union([
    z
        .object({
            type: z.literal("catalog-item"),
        })
        .and(SendChatMessageToMemberCatalogItemAttachmentSchema),
    z
        .object({
            type: z.literal("file"),
        })
        .and(SendChatMessageToMemberFileAttachmentSchema),
    z
        .object({
            type: z.literal("booking-suggestion"),
        })
        .and(SendChatMessageToMemberBookingSuggestionAttachmentSchema),
]);

export type SendChatMessageToMemberAttachment = z.infer<
    typeof SendChatMessageToMemberAttachmentSchema
>;

/**
 * Agent payload for sending a chat message to a member.
 */
export const SendChatMessageToMemberSchema = z.object({
    content: SendChatMessageToMemberContentSchema,
    attachments: z.array(SendChatMessageToMemberAttachmentSchema).optional(),
    idempotencyKey: z.string().min(1),
});

export type SendChatMessageToMember = z.infer<typeof SendChatMessageToMemberSchema>;

/**
 * Optional scope for AI summary generation.
 */
export const GenerateMemberSummaryRequestSchema = z.object({
    requestId: z.string().min(1).optional(),
});

export type GenerateMemberSummaryRequest = z.infer<
    typeof GenerateMemberSummaryRequestSchema
>;

export const GenerateMemberSummaryResponseDataSchema = z.object({
    summaryId: z.string().nullable(),
});

export type GenerateMemberSummaryResponseData = z.infer<
    typeof GenerateMemberSummaryResponseDataSchema
>;

export const SuccessResponseGenerateMemberSummarySchema = z.object({
    success: z.literal(true),
    data: GenerateMemberSummaryResponseDataSchema,
});

export type SuccessResponseGenerateMemberSummary = z.infer<
    typeof SuccessResponseGenerateMemberSummarySchema
>;

/**
 * attachmentIndex selects which attachment on the message to stream (0-based, decimal string). Filename and content type for the response come from the stored message attachment metadata.
 */
export const DownloadMessageAttachmentQuerySchema = z.object({
    attachmentIndex: z.string().regex(/^(0|[1-9][0-9]*)$/),
});

export type DownloadMessageAttachmentQuery = z.infer<
    typeof DownloadMessageAttachmentQuerySchema
>;

export const ErrorResponseSchema = z.object({
    success: z.literal(false),
    error: z.string(),
});

export type ErrorResponse = z.infer<typeof ErrorResponseSchema>;

/**
 * ISO 4217 three-letter currency code
 */
export const CurrencyCodeSchema = z.enum([
    "AED",
    "AFN",
    "ALL",
    "AMD",
    "ANG",
    "AOA",
    "ARS",
    "AUD",
    "AWG",
    "AZN",
    "BAM",
    "BBD",
    "BDT",
    "BGN",
    "BHD",
    "BIF",
    "BMD",
    "BND",
    "BOB",
    "BOV",
    "BRL",
    "BSD",
    "BTN",
    "BWP",
    "BYN",
    "BZD",
    "CAD",
    "CDF",
    "CHF",
    "CHW",
    "CLF",
    "CLP",
    "CNY",
    "COP",
    "COU",
    "CRC",
    "CUC",
    "CUP",
    "CVE",
    "CZK",
    "DJF",
    "DKK",
    "DOP",
    "DZD",
    "EGP",
    "ERN",
    "ETB",
    "EUR",
    "FJD",
    "FKP",
    "GBP",
    "GEL",
    "GHS",
    "GIP",
    "GMD",
    "GNF",
    "GTQ",
    "GYD",
    "HKD",
    "HNL",
    "HRK",
    "HTG",
    "HUF",
    "IDR",
    "ILS",
    "INR",
    "IQD",
    "IRR",
    "ISK",
    "JMD",
    "JOD",
    "JPY",
    "KES",
    "KGS",
    "KHR",
    "KMF",
    "KPW",
    "KRW",
    "KWD",
    "KYD",
    "KZT",
    "LAK",
    "LBP",
    "LKR",
    "LRD",
    "LSL",
    "LYD",
    "MAD",
    "MDL",
    "MGA",
    "MKD",
    "MMK",
    "MNT",
    "MOP",
    "MRU",
    "MUR",
    "MVR",
    "MWK",
    "MXN",
    "MXV",
    "MYR",
    "MZN",
    "NAD",
    "NGN",
    "NIO",
    "NOK",
    "NPR",
    "NZD",
    "OMR",
    "PAB",
    "PEN",
    "PGK",
    "PHP",
    "PKR",
    "PLN",
    "PYG",
    "QAR",
    "RON",
    "RSD",
    "RUB",
    "RWF",
    "SAR",
    "SBD",
    "SCR",
    "SDG",
    "SEK",
    "SGD",
    "SHP",
    "SLL",
    "SOS",
    "SRD",
    "SSP",
    "STN",
    "SVC",
    "SYP",
    "SZL",
    "THB",
    "TJS",
    "TMT",
    "TND",
    "TOP",
    "TRY",
    "TTD",
    "TWD",
    "TZS",
    "UAH",
    "UGX",
    "USD",
    "USN",
    "UYI",
    "UYU",
    "UYW",
    "UZS",
    "VES",
    "VND",
    "VUV",
    "WST",
    "XAF",
    "XCD",
    "XDR",
    "XOF",
    "XPF",
    "XSU",
    "XUA",
    "YER",
    "ZAR",
    "ZMW",
    "ZWL",
]);

export type CurrencyCode = z.infer<typeof CurrencyCodeSchema>;

/**
 * Price fields for a booking response.
 */
export const BookingPriceDetailsSchema = z.object({
    amount: z.number().gte(0).nullable(),
    currency: CurrencyCodeSchema.nullable(),
    quote: z.union([z.literal("final"), z.literal("estimate")]).nullable(),
    paymentTo: z.union([z.literal("albertine"), z.literal("supplier")]).nullable(),
    details: z.string().nullable(),
});

export type BookingPriceDetails = z.infer<typeof BookingPriceDetailsSchema>;

export const BookingPriceSchema = z.object({
    amount: z.number().gte(0),
    currency: CurrencyCodeSchema,
});

export type BookingPrice = z.infer<typeof BookingPriceSchema>;

export const BookingOptionInputSchema = z.object({
    id: z.uuid(),
    title: z.string(),
    description: z.string().nullish(),
    images: z.array(BookingAttachmentInputSchema).nullish(),
    price: BookingPriceSchema.nullish(),
    confirmed: z.boolean().nullish(),
    memberPreferred: z.boolean().nullish(),
});

export type BookingOptionInput = z.infer<typeof BookingOptionInputSchema>;

/**
 * Partial booking update. Omitted fields are left unchanged. `null` clears a
 * nullable field.
 *
 * Not updatable via this contract (derived or set by the server):
 * - `offeringCategory`
 * - `notesForMemberSenderId` / `notesForMemberSenderName` (set from the authenticated agent when `notesForMember` changes)
 * - `country`, `googleUrl`, `geoPoint`, `numberOfNights` (not edited from LMT booking UI)
 * - `archived` (use `POST /bookings/{bookingId}/archive`)
 * - `reservationStatus` (use `POST /bookings/{bookingId}/status`)
 * - `images`, `imageURL`, `croppedImageURL` (use `PUT /bookings/{bookingId}/images`)
 * - `attachments` (use `PUT /bookings/{bookingId}/attachments`)
 *
 * Nested `options[].images` remain part of this contract: they belong to a booking
 * option payload rather than the booking gallery.
 *
 */
export const UpdateBookingRequestSchema = z.object({
    title: z.string().nullish(),
    description: z.string().nullish(),
    website: z.string().nullish(),
    phoneNumber: z.string().nullish(),
    address: z.string().nullish(),
    numberOfGuests: z.number().nullish(),
    rooms: z.string().nullish(),
    price: BookingPriceSchema.nullish(),
    priceCurrency: CurrencyCodeSchema.nullish(),
    priceQuote: z.union([z.literal("final"), z.literal("estimate")]).nullish(),
    paymentTo: z.union([z.literal("albertine"), z.literal("supplier")]).nullish(),
    priceDetails: z.string().nullish(),
    reference: z.string().nullish(),
    notesForMember: z.string().nullish(),
    supplierIds: z.array(z.string().min(1)).nullish(),
    supplierDetails: z.string().nullish(),
    startTime: z.iso.datetime().nullish(),
    startTimezone: z.string().nullish(),
    endTime: z.iso.datetime().nullish(),
    endTimezone: z.string().nullish(),
    reviewNeeded: z.boolean().nullish(),
    headline: z.string().nullish(),
    benefits: z.string().nullish(),
    bookingPolicy: z.string().nullish(),
    options: z.array(BookingOptionInputSchema).nullish(),
});

export type UpdateBookingRequest = z.infer<typeof UpdateBookingRequestSchema>;

/**
 * Request body for creating a payment request.
 */
export const CreatePaymentBodySchema = z.object({
    amount: z.number().gt(0),
    currency: CurrencyCodeSchema,
    paymentMethod: z.string(),
    supplierAndCommission: z.string(),
    otherDetails: z.string().nullish(),
    invoiceName: z.string().max(200).optional(),
});

export type CreatePaymentBody = z.infer<typeof CreatePaymentBodySchema>;

/**
 * Lifecycle of a payment request from initial request through Stripe / Postgres
 * (includes terminal paid state).
 *
 */
export const PaymentStatusSchema = z.enum([
    "PAYMENT_REQUESTED",
    "DRAFT_INVOICE_CREATED",
    "INVOICE_FINALIZED",
    "INVOICE_PAID",
]);

export type PaymentStatus = z.infer<typeof PaymentStatusSchema>;

/**
 * Response for a created payment request.
 */
export const PaymentResponseSchema = z.object({
    success: z.boolean(),
    status: PaymentStatusSchema,
});

export type PaymentResponse = z.infer<typeof PaymentResponseSchema>;

/**
 * Payment method category. `payment-link` and `other` are stored as-is in
 * `payment_method`; `card` matches Stripe payment method ids (`pm_*`, `src_*`, `card_*`).
 *
 */
export const PaymentMethodSchema = z.enum(["payment-link", "other", "card"]);

export type PaymentMethod = z.infer<typeof PaymentMethodSchema>;

export const ChargePaymentResponseDataSchema = z.object({
    success: z.literal(true),
    hostedInvoiceUrl: z.string().nullish(),
});

export type ChargePaymentResponseData = z.infer<typeof ChargePaymentResponseDataSchema>;

export const SuccessResponseChargePaymentSchema = z.object({
    success: z.literal(true),
    data: ChargePaymentResponseDataSchema,
});

export type SuccessResponseChargePayment = z.infer<
    typeof SuccessResponseChargePaymentSchema
>;

export const MarkPaymentAsPaidResponseDataSchema = z.object({
    success: z.literal(true),
});

export type MarkPaymentAsPaidResponseData = z.infer<
    typeof MarkPaymentAsPaidResponseDataSchema
>;

export const SuccessResponseMarkPaymentAsPaidSchema = z.object({
    success: z.literal(true),
    data: MarkPaymentAsPaidResponseDataSchema,
});

export type SuccessResponseMarkPaymentAsPaid = z.infer<
    typeof SuccessResponseMarkPaymentAsPaidSchema
>;

export const ListAllPaymentsSortFieldSchema = z.enum([
    "createdAt",
    "amount",
    "currency",
    "paymentMethod",
    "createdBy",
]);

export type ListAllPaymentsSortField = z.infer<typeof ListAllPaymentsSortFieldSchema>;

/**
 * Query parameters for GET /payments (admin **Payments** list).
 */
export const ListAllPaymentsQuerySchema = z.object({
    sortBy: ListAllPaymentsSortFieldSchema.optional().default("createdAt"),
    sortOrder: z.enum(["asc", "desc"]).optional(),
    page: z.coerce.number().int().gte(1).optional().default(1),
    pageSize: z.coerce.number().int().gte(1).lte(100).optional().default(50),
    currency: z
        .preprocess((val) => {
            if (!val) return undefined;
            return Array.isArray(val) ? val : [String(val)];
        }, z.array(CurrencyCodeSchema))
        .optional(),
    paymentMethod: z
        .preprocess((val) => {
            if (!val) return undefined;
            return Array.isArray(val) ? val : [String(val)];
        }, z.array(PaymentMethodSchema))
        .optional(),
    createdBy: z
        .preprocess((val) => {
            if (!val) return undefined;
            return Array.isArray(val) ? val : [String(val)];
        }, z.array(z.string()))
        .optional(),
    status: z
        .preprocess((val) => {
            if (!val) return undefined;
            return Array.isArray(val) ? val : [String(val)];
        }, z.array(PaymentStatusSchema))
        .optional(),
});

export type ListAllPaymentsQuery = z.infer<typeof ListAllPaymentsQuerySchema>;

/**
 * Row for GET /payments (admin list; no supplier or Slack columns).
 * `requestId` is taken from the linked booking document when accessible.
 *
 */
export const ListAllPaymentsItemSchema = z.object({
    id: z.string(),
    requestId: z.string().nullable(),
    createdBy: z.string().nullish(),
    createdByName: z.string().nullish(),
    amount: z.number(),
    currency: CurrencyCodeSchema,
    paymentMethod: z.string(),
    status: PaymentStatusSchema,
    otherDetails: z.string().nullish(),
    createdAt: z.iso.datetime(),
});

export type ListAllPaymentsItem = z.infer<typeof ListAllPaymentsItemSchema>;

export const ListAllPaymentsDataSchema = z.object({
    total: z.int(),
    payments: z.array(ListAllPaymentsItemSchema),
});

export type ListAllPaymentsData = z.infer<typeof ListAllPaymentsDataSchema>;

export const SuccessResponseListAllPaymentsSchema = z.object({
    success: z.literal(true),
    data: ListAllPaymentsDataSchema,
});

export type SuccessResponseListAllPayments = z.infer<
    typeof SuccessResponseListAllPaymentsSchema
>;

export const ListMembersSortFieldSchema = z.enum([
    "name",
    "lastSeenOnline",
    "creationDate",
    "membershipTrialExpiry",
    "activeRequests",
    "messageByMember",
    "messageByAgent",
]);

export type ListMembersSortField = z.infer<typeof ListMembersSortFieldSchema>;

export const ListMembersMemberGroupSchema = z.enum([
    "default",
    "assigned-to-me",
    "archived",
    "lapsed-and-churned",
]);

export type ListMembersMemberGroup = z.infer<typeof ListMembersMemberGroupSchema>;

export const ListMembersEngagementLevelSchema = z.enum(["poor", "average", "good"]);

export type ListMembersEngagementLevel = z.infer<
    typeof ListMembersEngagementLevelSchema
>;

export const ListMembersAgentEngagementLevelSchema = z.enum([
    "messageSentWithinWeek",
    "messageSentWithinTwoWeek",
    "messageSentOverTwoWeek",
]);

export type ListMembersAgentEngagementLevel = z.infer<
    typeof ListMembersAgentEngagementLevelSchema
>;

export const ListMembersRequestFilterSchema = z.enum([
    "activeRequests",
    "noActiveRequests",
    "noRequestsYet",
    "hasHadRequests",
    "noBookingsYet",
]);

export type ListMembersRequestFilter = z.infer<typeof ListMembersRequestFilterSchema>;

export const ListMembersMembershipStageSchema = z.enum(["trial", "paid"]);

export type ListMembersMembershipStage = z.infer<
    typeof ListMembersMembershipStageSchema
>;

/**
 * Query parameters for GET /members (LMT members list view).
 */
export const ListMembersQuerySchema = z.object({
    continuationToken: z.string().optional(),
    pageSize: z.coerce.number().int().gte(1).lte(100).optional().default(50),
    memberGroup: ListMembersMemberGroupSchema.optional().default("default"),
    sortBy: ListMembersSortFieldSchema.optional().default("name"),
    sortOrder: z.enum(["asc", "desc"]).optional(),
    search: z.string().min(3).optional(),
    groups: z
        .preprocess((val) => {
            if (!val) return undefined;
            return Array.isArray(val) ? val : [String(val)];
        }, z.array(z.string()))
        .optional(),
    engagement: z
        .preprocess((val) => {
            if (!val) return undefined;
            return Array.isArray(val) ? val : [String(val)];
        }, z.array(ListMembersEngagementLevelSchema))
        .optional(),
    assignedTo: z
        .preprocess((val) => {
            if (!val) return undefined;
            return Array.isArray(val) ? val : [String(val)];
        }, z.array(z.string()))
        .optional(),
    teamIds: z
        .preprocess((val) => {
            if (!val) return undefined;
            return Array.isArray(val) ? val : [String(val)];
        }, z.array(z.string()))
        .optional(),
    requests: z
        .preprocess((val) => {
            if (!val) return undefined;
            return Array.isArray(val) ? val : [String(val)];
        }, z.array(ListMembersRequestFilterSchema))
        .optional(),
    agentEngagement: z
        .preprocess((val) => {
            if (!val) return undefined;
            return Array.isArray(val) ? val : [String(val)];
        }, z.array(ListMembersAgentEngagementLevelSchema))
        .optional(),
    membershipTiers: z
        .preprocess((val) => {
            if (!val) return undefined;
            return Array.isArray(val) ? val : [String(val)];
        }, z.array(z.string()))
        .optional(),
    membershipStages: z
        .preprocess((val) => {
            if (!val) return undefined;
            return Array.isArray(val) ? val : [String(val)];
        }, z.array(ListMembersMembershipStageSchema))
        .optional(),
});

export type ListMembersQuery = z.infer<typeof ListMembersQuerySchema>;

export const MemberListItemDeviceLocationPlaceSchema = z.object({
    city: z.string(),
    isoCountryCode: z.string(),
});

export type MemberListItemDeviceLocationPlace = z.infer<
    typeof MemberListItemDeviceLocationPlaceSchema
>;

/**
 * Cancellation metadata for a member's subscription.
 */
export const MembershipCancellationSchema = z.object({
    reason: z.string(),
    additionalNote: z.string().nullish(),
    cancelledAt: z.iso.datetime().nullish(),
    cancelledBy: z.string(),
});

export type MembershipCancellation = z.infer<typeof MembershipCancellationSchema>;

/**
 * Member subscription snapshot. Trial memberships use a trial period value; paid memberships use `no-trial`.
 *
 */
export const MembershipSchema = z.object({
    tier: z.string(),
    trial: z.enum(["trial-14-days", "trial-1-month", "trial-3-month", "no-trial"]),
    startTrialDate: z.iso.date().nullish(),
    endTrialDate: z.iso.date().nullish(),
    startDate: z.iso.date().nullish(),
    endDate: z.iso.date().nullish(),
    commitmentPeriod: z.string().nullish(),
    billingPeriod: z.enum(["monthly", "yearly"]).nullish(),
    cancellation: MembershipCancellationSchema.nullish(),
    extendedFor: z.int().nullish(),
});

export type Membership = z.infer<typeof MembershipSchema>;

export const UpdateMemberMembershipRequestSchema = z.object({
    membership: MembershipSchema,
});

export type UpdateMemberMembershipRequest = z.infer<
    typeof UpdateMemberMembershipRequestSchema
>;

/**
 * Subset of member data for the LMT members list view.
 */
export const MemberListItemSchema = z.object({
    id: z.string(),
    conversationId: z.string(),
    tenant: z.string(),
    title: z.string().nullable(),
    firstName: z.string(),
    lastName: z.string(),
    createdAt: z.iso.datetime(),
    lastSeenAt: z.iso.datetime().nullable(),
    assignedTo: z.string().nullable(),
    archived: z.boolean().nullable(),
    activeRequests: z.int(),
    totalRequests: z.int(),
    confirmedBookingsCount: z.int(),
    city: z.string().nullable(),
    country: z.string().nullable(),
    deviceLocationPlace: MemberListItemDeviceLocationPlaceSchema.nullable(),
    groups: z.array(z.string()).nullable(),
    membership: MembershipSchema.nullable(),
    lastMessageByMemberAt: z.iso.datetime().nullable(),
    lastMessageByAgentAt: z.iso.datetime().nullable(),
    lastMessageByAgentAgentId: z.string().nullable(),
    isTestMember: z.boolean(),
    isVipMember: z.boolean(),
});

export type MemberListItem = z.infer<typeof MemberListItemSchema>;

export const MemberFuturePlansSchema = z.object({
    plans: z.string(),
    updatedAt: z.iso.datetime(),
    updatedById: z.string(),
    updatedName: z.string(),
});

export type MemberFuturePlans = z.infer<typeof MemberFuturePlansSchema>;

export const MemberRatingScoreSchema = z.object({
    average: z.number(),
    ratingsCount: z.int(),
});

export type MemberRatingScore = z.infer<typeof MemberRatingScoreSchema>;

export const MemberLinkedFamilyMemberSchema = z.object({
    id: z.uuid(),
    type: z.literal("linked_profile"),
    memberId: z.string(),
});

export type MemberLinkedFamilyMember = z.infer<typeof MemberLinkedFamilyMemberSchema>;

export const MemberLimitedFamilyMemberSchema = z.object({
    id: z.uuid(),
    type: z.literal("limited_profile"),
    firstName: z.string().min(1),
    lastName: z.string().min(1),
    relationship: z.string().min(3),
    mustKnow: z.string().nullish(),
    dateOfBirth: z.string().nullish(),
    passportName: z.string().nullish(),
    passportNumber: z.string().nullish(),
    passportExpiryDate: z.string().nullish(),
    nationality: z.string().nullish(),
    frequentFlyerNumber: z.string().nullish(),
});

export type MemberLimitedFamilyMember = z.infer<typeof MemberLimitedFamilyMemberSchema>;

export const MemberFamilyMemberSchema = z.union([
    MemberLimitedFamilyMemberSchema,
    MemberLinkedFamilyMemberSchema,
]);

export type MemberFamilyMember = z.infer<typeof MemberFamilyMemberSchema>;

/**
 * Last device location reported by the member's app.
 */
export const MemberDeviceLocationSchema = z.object({
    latitude: z.number(),
    longitude: z.number(),
    place: MemberListItemDeviceLocationPlaceSchema.nullish(),
    updatedAt: z.iso.datetime().nullish(),
});

export type MemberDeviceLocation = z.infer<typeof MemberDeviceLocationSchema>;

export const ListMembersFacetCountSchema = z.object({
    id: z.string(),
    count: z.int(),
});

export type ListMembersFacetCount = z.infer<typeof ListMembersFacetCountSchema>;

export const ListMembersFacetsSchema = z.object({
    assignees: z.array(ListMembersFacetCountSchema),
    engagement: z.array(ListMembersFacetCountSchema),
    requests: z.array(ListMembersFacetCountSchema),
    agentEngagement: z.array(ListMembersFacetCountSchema),
    membershipTiers: z.array(ListMembersFacetCountSchema),
    membershipStages: z.array(ListMembersFacetCountSchema),
});

export type ListMembersFacets = z.infer<typeof ListMembersFacetsSchema>;

export const ListMembersDataSchema = z.object({
    items: z.array(MemberListItemSchema),
    continuationToken: z.string().nullable(),
    total: z.int(),
    facets: ListMembersFacetsSchema,
});

export type ListMembersData = z.infer<typeof ListMembersDataSchema>;

export const SuccessResponseListMembersSchema = z.object({
    success: z.literal(true),
    data: ListMembersDataSchema,
});

export type SuccessResponseListMembers = z.infer<
    typeof SuccessResponseListMembersSchema
>;

/**
 * Reduced member record for name, membership, and assignment lookups.
 */
export const ListAllMembersItemSchema = z.object({
    id: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    title: z.string().nullable(),
    assignedToAgentId: z.string().nullable(),
    membership: MembershipSchema.nullable(),
    archived: z.boolean(),
    isTestMember: z.boolean(),
    isVipMember: z.boolean(),
});

export type ListAllMembersItem = z.infer<typeof ListAllMembersItemSchema>;

export const ListAllMembersDataSchema = z.object({
    items: z.array(ListAllMembersItemSchema),
});

export type ListAllMembersData = z.infer<typeof ListAllMembersDataSchema>;

export const SuccessResponseListAllMembersSchema = z.object({
    success: z.literal(true),
    data: ListAllMembersDataSchema,
});

export type SuccessResponseListAllMembers = z.infer<
    typeof SuccessResponseListAllMembersSchema
>;

/**
 * A payment request record.
 */
export const PaymentSchema = z.object({
    id: z.string(),
    bookingId: z.string(),
    createdBy: z.string().nullish(),
    createdByName: z.string().nullish(),
    amount: z.number(),
    currency: CurrencyCodeSchema,
    paymentMethod: z.string(),
    slackUrl: z.string().nullish(),
    supplierAndCommission: z.string(),
    invoiceName: z.string(),
    otherDetails: z.string().nullish(),
    status: PaymentStatusSchema,
    hostedInvoiceUrl: z.string().nullish(),
    createdAt: z.iso.datetime(),
});

export type Payment = z.infer<typeof PaymentSchema>;

/**
 * Stripe card brand identifier
 */
export const CardBrandSchema = z.enum([
    "amex",
    "diners",
    "discover",
    "eftpos_au",
    "jcb",
    "mastercard",
    "unionpay",
    "visa",
    "unknown",
]);

export type CardBrand = z.infer<typeof CardBrandSchema>;

/**
 * A saved Stripe card payment method for the member.
 */
export const CardPaymentMethodSchema = z.object({
    id: z.string(),
    brand: CardBrandSchema,
    last4: z.string(),
    expMonth: z.int(),
    expYear: z.int(),
});

export type CardPaymentMethod = z.infer<typeof CardPaymentMethodSchema>;

export const SuccessResponseCardPaymentMethodsSchema = z.object({
    success: z.literal(true),
    data: z.array(CardPaymentMethodSchema),
});

export type SuccessResponseCardPaymentMethods = z.infer<
    typeof SuccessResponseCardPaymentMethodsSchema
>;

export const TenantSchema = z.enum([
    "albertine",
    "hugoboss",
    "mastercard",
    "carlo",
    "visa-i2",
    "revolut",
]);

export type Tenant = z.infer<typeof TenantSchema>;

/**
 * Query parameters for `GET .../payment-customer` (validated on the server with the generated Zod schema).
 */
export const FindMemberPaymentCustomerQuerySchema = z.object({
    tenant: TenantSchema,
    stripeCustomerId: z.string(),
});

export type FindMemberPaymentCustomerQuery = z.infer<
    typeof FindMemberPaymentCustomerQuerySchema
>;

/**
 * Stripe customer summary for the member's payments customer.
 */
export const PaymentCustomerSchema = z.object({
    id: z.string(),
    name: z.string().nullable(),
    email: z.string().nullable(),
    currency: z.string().nullable(),
});

export type PaymentCustomer = z.infer<typeof PaymentCustomerSchema>;

export const SuccessResponsePaymentCustomerSchema = z.object({
    success: z.literal(true),
    data: PaymentCustomerSchema.nullable(),
});

export type SuccessResponsePaymentCustomer = z.infer<
    typeof SuccessResponsePaymentCustomerSchema
>;

/**
 * A supplier row from the LMT catalog suppliers list.
 */
export const SupplierSchema = z.object({
    id: z.string(),
    name: z.string(),
    description: z.string(),
    website: z.string(),
    commissionAgreement: z.string(),
});

export type Supplier = z.infer<typeof SupplierSchema>;

export const ListSuppliersResponseDataSchema = z.object({
    results: z.array(SupplierSchema),
});

export type ListSuppliersResponseData = z.infer<typeof ListSuppliersResponseDataSchema>;

export const AgentRoleSchema = z.enum(["agent", "admin"]);

export type AgentRole = z.infer<typeof AgentRoleSchema>;

/**
 * Agent record for lists and assignee filters.
 */
export const AgentListItemSchema = z.object({
    id: z.string(),
    name: z.string(),
    firstName: z.string(),
    email: z.email(),
    role: AgentRoleSchema,
    disabled: z.boolean(),
    createdAt: z.iso.datetime(),
    permissionGroup: z.string().nullish(),
    avatarFullPath: z.string().nullish(),
    avatarThumbnailPath: z.string().nullish(),
    slackId: z.string().nullish(),
    onboardingCallReservationUrl: z.url().nullable(),
    authProvider: z.enum(["google"]).optional(),
    teamId: z.uuid().nullable(),
});

export type AgentListItem = z.infer<typeof AgentListItemSchema>;

export const GetAllAgentsResponseDataSchema = z.object({
    results: z.array(AgentListItemSchema),
});

export type GetAllAgentsResponseData = z.infer<typeof GetAllAgentsResponseDataSchema>;

export const SuccessResponseGetAllAgentsSchema = z.object({
    success: z.literal(true),
    data: GetAllAgentsResponseDataSchema,
});

export type SuccessResponseGetAllAgents = z.infer<
    typeof SuccessResponseGetAllAgentsSchema
>;

export const CreateAgentRequestSchema = z.object({
    firstName: z.string().min(2),
    lastName: z.string().min(2),
    email: z.email(),
    slackId: z.string().min(1),
    onboardingCallReservationUrl: z.url().optional(),
    teamId: z.uuid().optional(),
});

export type CreateAgentRequest = z.infer<typeof CreateAgentRequestSchema>;

export const CreateAgentResponseDataSchema = z.object({
    id: z.string(),
});

export type CreateAgentResponseData = z.infer<typeof CreateAgentResponseDataSchema>;

export const SuccessResponseCreateAgentSchema = z.object({
    success: z.literal(true),
    data: CreateAgentResponseDataSchema,
});

export type SuccessResponseCreateAgent = z.infer<
    typeof SuccessResponseCreateAgentSchema
>;

export const UpdateMyAgentProfileRequestSchema = z.object({
    slackId: z.string().nullish(),
    onboardingCallReservationUrl: z.url().nullish(),
    avatarFullPath: z.string().nullish(),
    avatarThumbnailPath: z.string().nullish(),
});

export type UpdateMyAgentProfileRequest = z.infer<
    typeof UpdateMyAgentProfileRequestSchema
>;

/**
 * Admin update for an agent. Email is not updatable.
 *
 */
export const UpdateAgentRequestSchema = z.object({
    firstName: z.string().min(2),
    lastName: z.string().min(2),
    teamId: z.uuid().nullable(),
    slackId: z.string().min(1),
    onboardingCallReservationUrl: z.url().nullish(),
});

export type UpdateAgentRequest = z.infer<typeof UpdateAgentRequestSchema>;

export const UpdateAgentPermissionGroupRequestSchema = z.object({
    permissionGroup: z.string().min(1),
});

export type UpdateAgentPermissionGroupRequest = z.infer<
    typeof UpdateAgentPermissionGroupRequestSchema
>;

export const AssignAgentToTeamRequestSchema = z.object({
    teamId: z.uuid().nullable(),
});

export type AssignAgentToTeamRequest = z.infer<typeof AssignAgentToTeamRequestSchema>;

/**
 * Agent team record.
 */
export const AgentTeamSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime().nullable(),
    agentIds: z.array(z.string().min(1)),
});

export type AgentTeam = z.infer<typeof AgentTeamSchema>;

export const ListAgentTeamsResponseDataSchema = z.object({
    results: z.array(AgentTeamSchema),
});

export type ListAgentTeamsResponseData = z.infer<
    typeof ListAgentTeamsResponseDataSchema
>;

export const SuccessResponseListAgentTeamsSchema = z.object({
    success: z.literal(true),
    data: ListAgentTeamsResponseDataSchema,
});

export type SuccessResponseListAgentTeams = z.infer<
    typeof SuccessResponseListAgentTeamsSchema
>;

/**
 * Member group used for profile labels, list filters, and service-hours options.
 */
export const MemberGroupSchema = z.object({
    id: z.string().min(1),
    displayName: z.string(),
    description: z.string().nullable(),
});

export type MemberGroup = z.infer<typeof MemberGroupSchema>;

export const ListMemberGroupsResponseDataSchema = z.object({
    results: z.array(MemberGroupSchema),
});

export type ListMemberGroupsResponseData = z.infer<
    typeof ListMemberGroupsResponseDataSchema
>;

export const SuccessResponseListMemberGroupsSchema = z.object({
    success: z.literal(true),
    data: ListMemberGroupsResponseDataSchema,
});

export type SuccessResponseListMemberGroups = z.infer<
    typeof SuccessResponseListMemberGroupsSchema
>;

/**
 * Permission group option for assignment.
 */
export const PermissionGroupSchema = z.object({
    id: z.string().min(1),
    name: z.string(),
});

export type PermissionGroup = z.infer<typeof PermissionGroupSchema>;

export const ListPermissionGroupsResponseDataSchema = z.object({
    results: z.array(PermissionGroupSchema),
});

export type ListPermissionGroupsResponseData = z.infer<
    typeof ListPermissionGroupsResponseDataSchema
>;

export const SuccessResponseListPermissionGroupsSchema = z.object({
    success: z.literal(true),
    data: ListPermissionGroupsResponseDataSchema,
});

export type SuccessResponseListPermissionGroups = z.infer<
    typeof SuccessResponseListPermissionGroupsSchema
>;

/**
 * Desired state of a single team. Omit `id` to create a new team; include
 * `id` to rename an existing team.
 *
 */
export const SyncAgentTeamInputSchema = z.object({
    id: z.uuid().optional(),
    name: z.string().min(1),
});

export type SyncAgentTeamInput = z.infer<typeof SyncAgentTeamInputSchema>;

export const SyncAgentTeamsRequestSchema = z.object({
    teams: z.array(SyncAgentTeamInputSchema),
});

export type SyncAgentTeamsRequest = z.infer<typeof SyncAgentTeamsRequestSchema>;

export const SyncAgentsToKeycloakResponseDataSchema = z.object({
    totalAgents: z.int(),
    totalSynced: z.int(),
    totalFailed: z.int(),
    totalSkipped: z.int(),
});

export type SyncAgentsToKeycloakResponseData = z.infer<
    typeof SyncAgentsToKeycloakResponseDataSchema
>;

export const SuccessResponseSyncAgentsToKeycloakSchema = z.object({
    success: z.literal(true),
    data: SyncAgentsToKeycloakResponseDataSchema,
});

export type SuccessResponseSyncAgentsToKeycloak = z.infer<
    typeof SuccessResponseSyncAgentsToKeycloakSchema
>;

export const EnsureAppReviewMembersResponseDataSchema = z.object({
    ensuredTenants: z.array(TenantSchema),
    failedTenants: z.array(TenantSchema),
});

export type EnsureAppReviewMembersResponseData = z.infer<
    typeof EnsureAppReviewMembersResponseDataSchema
>;

export const SuccessResponseEnsureAppReviewMembersSchema = z.object({
    success: z.literal(true),
    data: EnsureAppReviewMembersResponseDataSchema,
});

export type SuccessResponseEnsureAppReviewMembers = z.infer<
    typeof SuccessResponseEnsureAppReviewMembersSchema
>;

export const SuccessResponseSuppliersSchema = z.object({
    success: z.literal(true),
    data: ListSuppliersResponseDataSchema,
});

export type SuccessResponseSuppliers = z.infer<typeof SuccessResponseSuppliersSchema>;

/**
 * An active membership tier row exposed to agents for catalog and member flows.
 */
export const MembershipTierSchema = z.object({
    id: z.string(),
    name: z.string(),
    tenant: TenantSchema,
    active: z.boolean(),
    default: z.boolean(),
});

export type MembershipTier = z.infer<typeof MembershipTierSchema>;

export const SuccessResponseMembershipsSchema = z.object({
    success: z.literal(true),
    data: z.array(MembershipTierSchema),
});

export type SuccessResponseMemberships = z.infer<
    typeof SuccessResponseMembershipsSchema
>;

export const ServiceHoursClockTimeSchema = z.object({
    hour: z.int().gte(0).lte(23),
    minute: z.int().gte(0).lte(59),
});

export type ServiceHoursClockTime = z.infer<typeof ServiceHoursClockTimeSchema>;

export const ServiceHoursDaySlotSchema = z.object({
    hours: z.object({
        opening: ServiceHoursClockTimeSchema,
        closing: ServiceHoursClockTimeSchema,
    }),
    enabled: z.boolean(),
});

export type ServiceHoursDaySlot = z.infer<typeof ServiceHoursDaySlotSchema>;

/**
 * Weekly hours keyed by weekday name. All seven days are required;
 * set `enabled` to false when there is no service that day.
 *
 */
export const ServiceHoursWeekdayMapSchema = z.object({
    sunday: ServiceHoursDaySlotSchema,
    monday: ServiceHoursDaySlotSchema,
    tuesday: ServiceHoursDaySlotSchema,
    wednesday: ServiceHoursDaySlotSchema,
    thursday: ServiceHoursDaySlotSchema,
    friday: ServiceHoursDaySlotSchema,
    saturday: ServiceHoursDaySlotSchema,
});

export type ServiceHoursWeekdayMap = z.infer<typeof ServiceHoursWeekdayMapSchema>;

export const ServiceHoursSettingSchema = z.object({
    id: z.string().min(1),
    serviceHours: ServiceHoursWeekdayMapSchema,
    enabled: z.boolean(),
    memberMessageTemplate: z.string(),
    exampleServiceHoursText: z.string(),
});

export type ServiceHoursSetting = z.infer<typeof ServiceHoursSettingSchema>;

export const UpsertServiceHoursRequestSchema = z.object({
    serviceHours: ServiceHoursWeekdayMapSchema,
    enabled: z.boolean(),
    memberMessageTemplate: z.string(),
});

export type UpsertServiceHoursRequest = z.infer<typeof UpsertServiceHoursRequestSchema>;

export const ListServiceHoursResponseDataSchema = z.object({
    results: z.array(ServiceHoursSettingSchema),
});

export type ListServiceHoursResponseData = z.infer<
    typeof ListServiceHoursResponseDataSchema
>;

export const SuccessResponseListServiceHoursSchema = z.object({
    success: z.literal(true),
    data: ListServiceHoursResponseDataSchema,
});

export type SuccessResponseListServiceHours = z.infer<
    typeof SuccessResponseListServiceHoursSchema
>;

export const SuccessResponseServiceHoursSettingSchema = z.object({
    success: z.literal(true),
    data: ServiceHoursSettingSchema,
});

export type SuccessResponseServiceHoursSetting = z.infer<
    typeof SuccessResponseServiceHoursSettingSchema
>;

export const CrawlWebsiteImagesRequestSchema = z.object({
    baseUrl: z.url(),
});

export type CrawlWebsiteImagesRequest = z.infer<typeof CrawlWebsiteImagesRequestSchema>;

export const CrawlWebsiteImagesResponseDataSchema = z.object({
    images: z.array(z.string()).nullable(),
});

export type CrawlWebsiteImagesResponseData = z.infer<
    typeof CrawlWebsiteImagesResponseDataSchema
>;

export const SuccessResponseCrawlWebsiteImagesSchema = z.object({
    success: z.literal(true),
    data: CrawlWebsiteImagesResponseDataSchema,
});

export type SuccessResponseCrawlWebsiteImages = z.infer<
    typeof SuccessResponseCrawlWebsiteImagesSchema
>;

export const GenerateCatalogItemContentRequestSchema = z.object({
    name: z.string().min(1),
    offeringCategory: z.string().min(1),
    address: z.string().nullable(),
    websiteUrl: z.string().nullable(),
});

export type GenerateCatalogItemContentRequest = z.infer<
    typeof GenerateCatalogItemContentRequestSchema
>;

export const GenerateCatalogItemContentResponseDataSchema = z.object({
    description: z.string(),
    localInsights: z.string(),
});

export type GenerateCatalogItemContentResponseData = z.infer<
    typeof GenerateCatalogItemContentResponseDataSchema
>;

export const SuccessResponseGenerateCatalogItemContentSchema = z.object({
    success: z.literal(true),
    data: GenerateCatalogItemContentResponseDataSchema,
});

export type SuccessResponseGenerateCatalogItemContent = z.infer<
    typeof SuccessResponseGenerateCatalogItemContentSchema
>;

export const SuccessResponsePaymentsSchema = z.object({
    success: z.literal(true),
    data: z.array(PaymentSchema),
});

export type SuccessResponsePayments = z.infer<typeof SuccessResponsePaymentsSchema>;

export const SuccessResponsePaymentResponseSchema = z.object({
    success: z.literal(true),
    data: PaymentResponseSchema,
});

export type SuccessResponsePaymentResponse = z.infer<
    typeof SuccessResponsePaymentResponseSchema
>;

export const SyncMembersToKeycloakFailedMemberSchema = z.object({
    memberId: z.string(),
    error: z.string(),
});

export type SyncMembersToKeycloakFailedMember = z.infer<
    typeof SyncMembersToKeycloakFailedMemberSchema
>;

export const SyncMembersToKeycloakResponseDataSchema = z.object({
    failedForMembers: z.array(SyncMembersToKeycloakFailedMemberSchema),
    syncedMemberIds: z.array(z.string()),
});

export type SyncMembersToKeycloakResponseData = z.infer<
    typeof SyncMembersToKeycloakResponseDataSchema
>;

export const SuccessResponseSyncMembersToKeycloakSchema = z.object({
    success: z.literal(true),
    data: SyncMembersToKeycloakResponseDataSchema,
});

export type SuccessResponseSyncMembersToKeycloak = z.infer<
    typeof SuccessResponseSyncMembersToKeycloakSchema
>;

export const BroadcastMessagesToMembersRequestSchema = z.object({
    message: z.string().min(1).max(2000),
    catalogItemIds: z.array(z.string()).max(10).optional(),
    memberIds: z.array(z.string()).min(1).max(200),
});

export type BroadcastMessagesToMembersRequest = z.infer<
    typeof BroadcastMessagesToMembersRequestSchema
>;

export const BroadcastMemberResultSchema = z.object({
    memberId: z.string(),
    status: z.enum([
        "sent",
        "member-not-found",
        "member-no-assigned-agent",
        "agent-not-found",
        "member-no-conversation",
        "failed",
    ]),
    messageId: z.string().optional(),
    pushSkipped: z.boolean().optional(),
    error: z.string().optional(),
});

export type BroadcastMemberResult = z.infer<typeof BroadcastMemberResultSchema>;

export const BroadcastMessagesToMembersResponseDataSchema = z.object({
    results: z.array(BroadcastMemberResultSchema),
});

export type BroadcastMessagesToMembersResponseData = z.infer<
    typeof BroadcastMessagesToMembersResponseDataSchema
>;

export const SuccessResponseBroadcastMessagesToMembersSchema = z.object({
    success: z.literal(true),
    data: BroadcastMessagesToMembersResponseDataSchema,
});

export type SuccessResponseBroadcastMessagesToMembers = z.infer<
    typeof SuccessResponseBroadcastMessagesToMembersSchema
>;

export const FindRevolutMembersByPromoCodesRequestSchema = z.object({
    promoCodes: z.array(z.string().min(1)).min(1).max(1000),
});

export type FindRevolutMembersByPromoCodesRequest = z.infer<
    typeof FindRevolutMembersByPromoCodesRequestSchema
>;

export const RevolutPromoCodeMemberSchema = z.object({
    promoCode: z.string(),
    memberId: z.string(),
    tenant: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    email: z.string(),
    phoneNumber: z.string(),
});

export type RevolutPromoCodeMember = z.infer<typeof RevolutPromoCodeMemberSchema>;

export const FindRevolutMembersByPromoCodesResponseDataSchema = z.object({
    members: z.array(RevolutPromoCodeMemberSchema),
    unmatchedPromoCodes: z.array(z.string()),
});

export type FindRevolutMembersByPromoCodesResponseData = z.infer<
    typeof FindRevolutMembersByPromoCodesResponseDataSchema
>;

export const SuccessResponseFindRevolutMembersByPromoCodesSchema = z.object({
    success: z.literal(true),
    data: FindRevolutMembersByPromoCodesResponseDataSchema,
});

export type SuccessResponseFindRevolutMembersByPromoCodes = z.infer<
    typeof SuccessResponseFindRevolutMembersByPromoCodesSchema
>;

export const TestTranslationResponseDataSchema = z.object({
    text: z.string(),
    detectedLanguage: z.string().nullable(),
    durationMs: z.int().gte(0),
});

export type TestTranslationResponseData = z.infer<
    typeof TestTranslationResponseDataSchema
>;

export const SuccessResponseTestTranslationSchema = z.object({
    success: z.literal(true),
    data: TestTranslationResponseDataSchema,
});

export type SuccessResponseTestTranslation = z.infer<
    typeof SuccessResponseTestTranslationSchema
>;

/**
 * Where this theme appears in Discover.
 */
export const ThemePlacementSchema = z.enum([
    "cityGuide",
    "spotlight",
    "category",
    "personalised",
]);

export type ThemePlacement = z.infer<typeof ThemePlacementSchema>;

/**
 * Tenant a theme content variation applies to, or "default" for the content shown to every tenant without its own variation.
 *
 */
export const ThemeContentTenantSchema = z.union([TenantSchema, z.literal("default")]);

export type ThemeContentTenant = z.infer<typeof ThemeContentTenantSchema>;

/**
 * Static content where catalog items are explicitly curated.
 */
export const StaticThemeContentSchema = z.object({
    type: z.literal("static"),
    tenant: ThemeContentTenantSchema,
    catalogItemIds: z.array(z.string()),
});

export type StaticThemeContent = z.infer<typeof StaticThemeContentSchema>;

/**
 * Static content input for create/update requests. Requires at least 1 catalog item.
 */
export const StaticThemeContentInputSchema = z.object({
    type: z.literal("static"),
    tenant: ThemeContentTenantSchema,
    catalogItemIds: z.array(z.string()).min(1),
});

export type StaticThemeContentInput = z.infer<typeof StaticThemeContentInputSchema>;

/**
 * Dynamic content where catalog items are resolved by filter.
 */
export const DynamicThemeContentSchema = z.object({
    type: z.literal("dynamic"),
    tenant: ThemeContentTenantSchema,
});

export type DynamicThemeContent = z.infer<typeof DynamicThemeContentSchema>;

/**
 * How catalog items for this theme are chosen.
 */
export const ThemeContentSchema = z.union([
    z
        .object({
            type: z.literal("static"),
        })
        .and(StaticThemeContentSchema),
    z
        .object({
            type: z.literal("dynamic"),
        })
        .and(DynamicThemeContentSchema),
]);

export type ThemeContent = z.infer<typeof ThemeContentSchema>;

export const ThemePreferenceSchema = z.enum([
    "travel",
    "wellness",
    "dining",
    "sportsEvents",
    "culturalEvents",
    "localExperiences",
]);

export type ThemePreference = z.infer<typeof ThemePreferenceSchema>;

export const ThemeLocationSchema = z.object({
    city: z.string().nullable(),
    country: z.string(),
    countryCode: z.string().max(2),
});

export type ThemeLocation = z.infer<typeof ThemeLocationSchema>;

/**
 * Preferred cities and theme category scores for a member.
 */
export const MemberPreferencesSchema = z.object({
    topCities: z.array(ThemeLocationSchema).nullable(),
    preferences: z.record(z.string(), z.number()).nullable(),
});

export type MemberPreferences = z.infer<typeof MemberPreferencesSchema>;

export const SuccessResponseMemberPreferencesSchema = z.object({
    success: z.literal(true),
    data: MemberPreferencesSchema,
});

export type SuccessResponseMemberPreferences = z.infer<
    typeof SuccessResponseMemberPreferencesSchema
>;

export const ThemeGroupSchema = z.object({
    id: z.string(),
    name: z.string(),
});

export type ThemeGroup = z.infer<typeof ThemeGroupSchema>;

/**
 * Catalog-item filter for filter-based (dynamic) themes.
 */
export const ThemeFilterSchema = z.object({
    recentlyOpened: z.literal(true).optional(),
    categories: z.array(z.string()).optional(),
    cityGuideLocationKey: z.string().optional(),
    published: z.literal(true).optional(),
    albertineVerified: z.literal(true).optional(),
    order: z
        .object({
            by: z.enum(["openedAt", "publishedAt", "albertineVerifiedAt"]),
            direction: z.enum(["asc", "desc"]),
        })
        .optional(),
});

export type ThemeFilter = z.infer<typeof ThemeFilterSchema>;

/**
 * Content theme for LMT list and detail views.
 */
export const ThemeSchema = z.object({
    id: z.string(),
    name: z.string(),
    publishedAt: z.iso.datetime().nullable(),
    image: z.string().nullable(),
    content: z.array(ThemeContentSchema),
    placement: ThemePlacementSchema,
    group: ThemeGroupSchema.nullable(),
    location: z.array(ThemeLocationSchema),
    preferences: z.array(ThemePreferenceSchema),
});

export type Theme = z.infer<typeof ThemeSchema>;

/**
 * Payload to create a content theme. The content array must include the "default" variation and may include tenant-specific variations, at most one per tenant. Only static content can be created, each with at least 1 catalog item.
 *
 */
export const CreateThemeRequestSchema = z.object({
    name: z.string().min(3).max(255),
    content: z.array(StaticThemeContentInputSchema).min(1),
    preferences: z.array(ThemePreferenceSchema),
    location: z.array(ThemeLocationSchema),
    published: z.boolean(),
});

export type CreateThemeRequest = z.infer<typeof CreateThemeRequestSchema>;

/**
 * Partial update for a content theme. All fields are optional. When content is provided, it fully replaces the existing content variations - any tenant variation not included will be deleted. It must include the "default" variation and at most one variation per tenant. Dynamic variations cannot be created, removed, or changed to/from static.
 *
 */
export const UpdateThemeRequestSchema = z.object({
    name: z.string().min(3).max(255).optional(),
    content: z.array(StaticThemeContentInputSchema).min(1).optional(),
    preferences: z.array(ThemePreferenceSchema).optional(),
    location: z.array(ThemeLocationSchema).optional(),
    published: z.boolean().optional(),
});

export type UpdateThemeRequest = z.infer<typeof UpdateThemeRequestSchema>;

export const ListThemesResponseDataSchema = z.object({
    results: z.array(ThemeSchema),
});

export type ListThemesResponseData = z.infer<typeof ListThemesResponseDataSchema>;

export const SuccessResponseListThemesSchema = z.object({
    success: z.literal(true),
    data: ListThemesResponseDataSchema,
});

export type SuccessResponseListThemes = z.infer<typeof SuccessResponseListThemesSchema>;

export const SuccessResponseGetThemeSchema = z.object({
    success: z.literal(true),
    data: ThemeSchema,
});

export type SuccessResponseGetTheme = z.infer<typeof SuccessResponseGetThemeSchema>;

export const SuccessResponseCreateThemeSchema = z.object({
    success: z.literal(true),
    data: ThemeSchema,
});

export type SuccessResponseCreateTheme = z.infer<
    typeof SuccessResponseCreateThemeSchema
>;

export const SuccessResponseUpdateThemeSchema = z.object({
    success: z.literal(true),
    data: ThemeSchema,
});

export type SuccessResponseUpdateTheme = z.infer<
    typeof SuccessResponseUpdateThemeSchema
>;

export const GetThemeCatalogItemsQuerySchema = z.object({
    tenant: TenantSchema.optional(),
});

export type GetThemeCatalogItemsQuery = z.infer<typeof GetThemeCatalogItemsQuerySchema>;

/**
 * A sharing log entry for a catalog item.
 */
export const CatalogItemSharingLogEntrySchema = z.object({
    messageId: z.string(),
    memberId: z.string(),
    conversationId: z.string(),
    requestId: z.string().nullable(),
    sharedByAgentId: z.string().nullable(),
    sharedByAgent: z.boolean(),
    sharedAt: z.iso.datetime(),
});

export type CatalogItemSharingLogEntry = z.infer<
    typeof CatalogItemSharingLogEntrySchema
>;

/**
 * Service language code.
 */
export const ServiceLanguageSchema = z.enum(["en", "de", "fr"]);

export type ServiceLanguage = z.infer<typeof ServiceLanguageSchema>;

/**
 * New member details. `title`, `firstName`, and `lastName` are trimmed and repeated
 * whitespace is collapsed. `phoneNumber` must be a valid international number and is
 * stored in E.164 format.
 *
 */
export const CreateMemberRequestSchema = z.object({
    title: z.string().optional(),
    firstName: z.string(),
    lastName: z.string(),
    email: z.email().regex(/^[^A-Z]*$/),
    phoneNumber: z.string().min(1),
    membership: MembershipSchema,
    serviceLanguage: ServiceLanguageSchema.optional(),
    assignedAgentId: z.string().regex(/^[A-Za-z0-9]{28}$/),
    source: z
        .enum([
            "albertine-employee",
            "introducer",
            "investor-pool",
            "partnership",
            "member-referral",
            "event",
            "shareholder-referral",
            "other",
        ])
        .optional(),
    additionalNoteOnSource: z.string().optional(),
});

export type CreateMemberRequest = z.infer<typeof CreateMemberRequestSchema>;

/**
 * Member profile for the open-member workspace and profile panel.
 * Optional profile fields are omitted when they have never been set.
 *
 */
export const MemberSchema = z.object({
    id: z.string(),
    conversationId: z.string(),
    calendarId: z.string().nullable(),
    tenant: TenantSchema,
    title: z.string().nullish(),
    firstName: z.string(),
    lastName: z.string(),
    email: z.string(),
    phoneNumber: z.string(),
    city: z.string().nullish(),
    country: z.string().nullish(),
    assignedToId: z.string().nullish(),
    assignedToName: z.string().nullish(),
    agentFirstName: z.string().nullish(),
    archived: z.boolean(),
    archiveReason: z.string().nullish(),
    archivedAt: z.iso.datetime().nullish(),
    archivedById: z.string().nullish(),
    reactivatedAt: z.iso.datetime().nullish(),
    reactivatedById: z.string().nullish(),
    source: z
        .enum([
            "albertine-employee",
            "introducer",
            "investor-pool",
            "partnership",
            "member-referral",
            "event",
            "shareholder-referral",
            "other",
        ])
        .nullish(),
    additionalNoteOnSource: z.string().nullish(),
    referralId: z.string().nullish(),
    groups: z.array(z.string()).nullish(),
    membership: MembershipSchema.nullish(),
    createdAt: z.iso.datetime(),
    lastSeenAt: z.iso.datetime().nullish(),
    deviceLocation: MemberDeviceLocationSchema.nullish(),
    hasLoggedIn: z.boolean().nullish(),
    serviceLanguage: ServiceLanguageSchema.nullish(),
    appVersion: z.string().nullish(),
    appVersionAndroid: z.string().nullish(),
    androidVersion: z.string().nullish(),
    androidDevice: z.string().nullish(),
    iosVersion: z.string().nullish(),
    iosDevice: z.string().nullish(),
    pushNotificationsEnabled: z.boolean().optional(),
    pushNotificationsEnabledWeb: z.boolean().optional(),
    personalHighlight: z.string().nullish(),
    familyMustKnow: z.string().nullish(),
    personalMustKnow: z.string().nullish(),
    diningPreferences: z.string().nullish(),
    travelMustKnow: z.string().nullish(),
    eventsAndExperiences: z.string().nullish(),
    luxuryItemsAndCollectibles: z.string().nullish(),
    wellnessMustKnow: z.string().nullish(),
    diningFuturePlans: MemberFuturePlansSchema.nullish(),
    travelFuturePlans: MemberFuturePlansSchema.nullish(),
    address: z.string().nullish(),
    postalCode: z.string().nullish(),
    state: z.string().nullish(),
    timeZone: z.string().nullish(),
    nationality: z.string().nullish(),
    dateOfBirth: z.string().nullish(),
    passportName: z.string().nullish(),
    passportNumber: z.string().nullish(),
    passportExpiryDate: z.string().nullish(),
    airlinePreferences: z.string().nullish(),
    frequentFlyerNumber: z.string().nullish(),
    occupation: z.string().nullish(),
    memberships: z.string().nullish(),
    interests: z.string().nullish(),
    loves: z.string().nullish(),
    hates: z.string().nullish(),
    foodAllergies: z.string().nullish(),
    links: z.array(MemberLinkSchema).nullish(),
    familyMembers: z.array(MemberFamilyMemberSchema).nullish(),
    contributions: ContributionsSchema.nullish(),
    bookingsScore: MemberRatingScoreSchema.nullish(),
    serviceScore: MemberRatingScoreSchema.nullish(),
    secondaryPhone: z.string().nullish(),
    secondaryEmail: z.string().nullish(),
    preferredCommunicationChannel: MemberPreferredCommunicationChannelSchema.nullish(),
    channelContact: z.enum(["primary", "secondary"]).nullish(),
    eligibilityDetails: z
        .object({
            bin: z.string().nullish(),
            last4: z.string().nullish(),
        })
        .nullish(),
    onboardingCompletedVia: z.enum(["app", "phone"]).nullish(),
    onboardingCompletedAt: z.iso.datetime().nullish(),
});

export type Member = z.infer<typeof MemberSchema>;

export const SuccessResponseGetMemberSchema = z.object({
    success: z.literal(true),
    data: MemberSchema,
});

export type SuccessResponseGetMember = z.infer<typeof SuccessResponseGetMemberSchema>;

export const TestTranslationRequestSchema = z.object({
    text: z.string().min(1),
    sourceLanguage: ServiceLanguageSchema.optional(),
    targetLanguage: ServiceLanguageSchema,
    translator: z.enum(["chat-llm", "chat-nmt"]),
});

export type TestTranslationRequest = z.infer<typeof TestTranslationRequestSchema>;

/**
 * Translation language code (excludes English as it is the default language).
 */
export const TranslationLanguageSchema = z.enum(["de", "fr"]);

export type TranslationLanguage = z.infer<typeof TranslationLanguageSchema>;

/**
 * Translated fields for a catalog item in a specific language.
 */
export const CatalogItemTranslationSchema = z.object({
    name: z.string().nullable(),
    description: z.string().nullable(),
    address: z.string().nullable(),
    headline: z.string().nullable(),
    localInsights: z.string().nullable(),
    benefits: z.string().nullable(),
    bookingPolicy: z.string().nullable(),
    eventDates: z.string().nullable(),
    travelItineraryNotes: z.string().nullable(),
});

export type CatalogItemTranslation = z.infer<typeof CatalogItemTranslationSchema>;

/**
 * English catalog item content to translate into a target language.
 */
export const TranslateCatalogItemContentRequestSchema = z.object({
    language: TranslationLanguageSchema,
    content: CatalogItemTranslationSchema,
});

export type TranslateCatalogItemContentRequest = z.infer<
    typeof TranslateCatalogItemContentRequestSchema
>;

export const TranslateCatalogItemContentResponseDataSchema = z.object({
    translation: CatalogItemTranslationSchema,
});

export type TranslateCatalogItemContentResponseData = z.infer<
    typeof TranslateCatalogItemContentResponseDataSchema
>;

export const SuccessResponseTranslateCatalogItemContentSchema = z.object({
    success: z.literal(true),
    data: TranslateCatalogItemContentResponseDataSchema,
});

export type SuccessResponseTranslateCatalogItemContent = z.infer<
    typeof SuccessResponseTranslateCatalogItemContentSchema
>;

/**
 * Tag associated with a catalog item.
 */
export const CatalogItemTagSchema = z.enum([
    "price:free",
    "price:inexpensive",
    "price:moderate",
    "price:expensive",
    "price:very-expensive",
    "type:restaurant",
    "type:cafe",
    "type:pop-up",
    "type:bar",
    "type:pub",
    "type:wine-bar",
    "type:cocktail-bar",
    "type:rooftop",
    "type:nightclub",
    "type:live-music",
    "type:commercial-airline",
    "type:private-airline",
    "type:hotel",
    "type:villa",
    "type:apartment",
    "type:house",
    "type:resort",
    "type:yacht",
    "type:bed-and-breakfast",
    "type:car-rental",
    "type:car-transfer",
    "type:boat-rental",
    "type:charter-boat",
    "type:helicopter",
    "type:water-taxi",
    "type:sports",
    "type:theatre",
    "type:music",
    "type:fashion",
    "type:art",
    "type:dance",
    "type:museum",
    "type:art-gallery",
    "type:historical",
    "type:tourist-attraction",
    "type:must-do",
    "type:hidden-gem",
    "type:outdoors",
    "type:bookshop",
    "type:watches",
    "type:corporate-gifts",
    "type:personal-gifts",
    "type:collectibles",
    "type:spa",
    "type:sauna",
    "type:yoga",
    "type:ayurveda",
    "type:reiki",
    "type:massage",
    "type:beauty-salon",
    "type:personal-trainer",
    "type:retreat",
    "type:surfing",
    "type:wellness-hotel",
    "type:workout-studio",
    "type:flower-delivery",
    "type:cleaning-service",
    "type:moving-service",
    "type:real-estate",
    "type:education",
    "type:childcare",
    "type:bakery",
    "type:food-court",
    "vibe:boutique",
    "vibe:casual",
    "vibe:elegant",
    "vibe:high-end",
    "vibe:local-and-authentic",
    "vibe:luxury",
    "vibe:modern",
    "vibe:quirky",
    "vibe:romantic",
    "vibe:touristy",
    "vibe:traditional",
    "vibe:trendy",
    "vibe:fine-dining",
    "vibe:intimate",
    "goodFor:business",
    "goodFor:couples",
    "goodFor:dates",
    "goodFor:families",
    "goodFor:foodies",
    "goodFor:groups",
    "goodFor:locals",
    "goodFor:parties",
    "goodFor:private-dining",
    "goodFor:tourists",
    "goodFor:vegans",
    "goodFor:vegetarians",
    "goodFor:friends",
    "goodFor:friends-catchup",
    "goodFor:solo-trip",
    "meals:breakfast",
    "meals:brunch",
    "meals:lunch",
    "meals:dinner",
    "meals:quick-bites",
    "cuisine:african",
    "cuisine:american",
    "cuisine:asian",
    "cuisine:british",
    "cuisine:chinese",
    "cuisine:french",
    "cuisine:greek",
    "cuisine:indian",
    "cuisine:italian",
    "cuisine:japanese",
    "cuisine:latin",
    "cuisine:mediterranean",
    "cuisine:mexican",
    "cuisine:middle-eastern",
    "cuisine:scandinavian",
    "cuisine:seafood",
    "cuisine:spanish",
    "cuisine:steakhouse",
    "activities:adventurous",
    "activities:beach",
    "activities:city-break",
    "activities:cultural",
    "activities:hiking",
    "activities:nature",
    "activities:sea",
    "activities:safari",
    "activities:skiing",
]);

export type CatalogItemTag = z.infer<typeof CatalogItemTagSchema>;

/**
 * Offering category for a catalog item.
 */
export const OfferingCategorySchema = z.enum([
    "dining",
    "nightlife",
    "flight",
    "train",
    "accommodation",
    "travelItinerary",
    "transportation",
    "event",
    "localExperience",
    "shopping",
    "wellness",
    "services",
    "other",
]);

export type OfferingCategory = z.infer<typeof OfferingCategorySchema>;

/**
 * Query parameters for GET /catalog-items/search.
 */
export const SearchCatalogItemsQuerySchema = z.object({
    latitude: z.coerce.number().optional(),
    longitude: z.coerce.number().optional(),
    maxGeoDistanceKm: z.coerce.number().optional(),
    googlePlaceId: z.string().optional(),
    published: z.enum(["true", "false"]).optional(),
    nameSearchTerm: z.string().optional(),
    searchTerm: z.string().optional(),
    categories: z
        .preprocess((val) => {
            if (!val) return undefined;
            return Array.isArray(val) ? val : [String(val)];
        }, z.array(OfferingCategorySchema))
        .optional(),
    ids: z
        .preprocess((val) => {
            if (!val) return undefined;
            return Array.isArray(val) ? val : [String(val)];
        }, z.array(z.string()))
        .optional(),
    country: z.string().optional(),
    geoBoundingBoxTop: z.coerce.number().optional(),
    geoBoundingBoxBottom: z.coerce.number().optional(),
    geoBoundingBoxLeft: z.coerce.number().optional(),
    geoBoundingBoxRight: z.coerce.number().optional(),
    supplierLmtIds: z
        .preprocess((val) => {
            if (!val) return undefined;
            return Array.isArray(val) ? val : [String(val)];
        }, z.array(z.string()))
        .optional(),
    sources: z
        .preprocess((val) => {
            if (!val) return undefined;
            return Array.isArray(val) ? val : [String(val)];
        }, z.array(z.string()))
        .optional(),
    tags: z
        .preprocess((val) => {
            if (!val) return undefined;
            return Array.isArray(val) ? val : [String(val)];
        }, z.array(CatalogItemTagSchema))
        .optional(),
    albertineVerified: z.enum(["true", "false"]).optional(),
    recentlyOpened: z.enum(["true", "false"]).optional(),
    missingTranslations: z.enum(["true", "false"]).optional(),
    discoverableForTenant: z.union([TenantSchema, z.literal("all")]).optional(),
    pageSize: z.coerce.number().int().gte(1).optional(),
    cursor: z.string().optional(),
});

export type SearchCatalogItemsQuery = z.infer<typeof SearchCatalogItemsQuerySchema>;

/**
 * Address component for a catalog item.
 */
export const CatalogItemAddressComponentSchema = z.object({
    longName: z.string(),
    shortName: z.string(),
    types: z.array(z.string()),
});

export type CatalogItemAddressComponent = z.infer<
    typeof CatalogItemAddressComponentSchema
>;

/**
 * Geographic coordinates.
 */
export const GeoPointSchema = z.object({
    latitude: z.number(),
    longitude: z.number(),
});

export type GeoPoint = z.infer<typeof GeoPointSchema>;

/**
 * Booking detail from PostgreSQL.
 */
export const BookingSchema = z.object({
    id: z.string(),
    requestId: z.string(),
    memberId: z.string(),
    catalogItemId: z.string().nullable(),
    createdByAgentId: z.string(),
    archived: z.boolean(),
    title: z.string().nullable(),
    description: z.string().nullish(),
    website: z.string().nullish(),
    phoneNumber: z.string().nullish(),
    address: z.string().nullish(),
    country: z.string().nullish(),
    googleUrl: z.string().nullish(),
    geoPoint: GeoPointSchema.nullish(),
    offeringCategory: z.string().nullish(),
    numberOfGuests: z.number().nullish(),
    numberOfNights: z.number().nullish(),
    rooms: z.string().nullish(),
    price: BookingPriceDetailsSchema.nullish(),
    imageUrl: z.string().nullish(),
    croppedImageUrl: z.string().nullish(),
    absoluteCrop: BookingPixelCropSchema.nullish(),
    relativeCrop: BookingPercentCropSchema.nullish(),
    images: z.array(BookingAttachmentInputSchema).nullish(),
    attachments: z.array(BookingAttachmentInputSchema).nullish(),
    options: z.array(BookingOptionInputSchema).nullish(),
    reference: z.string().nullish(),
    reservationStatus: BookingReservationStatusSchema.nullable(),
    notes: BookingNotesSchema.nullish(),
    suppliers: BookingSuppliersSchema.nullish(),
    startTime: z.iso.datetime().nullish(),
    startTimezone: z.string().nullish(),
    endTime: z.iso.datetime().nullish(),
    endTimezone: z.string().nullish(),
    reviewNeeded: z.boolean().nullish(),
    headline: z.string().nullish(),
    benefits: z.string().nullish(),
    bookingPolicy: z.string().nullish(),
    calendarEventId: z.string().nullable(),
    proposal: BookingProposalSchema.nullish(),
    booking: BookingReviewSchema.nullish(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime().nullish(),
});

export type Booking = z.infer<typeof BookingSchema>;

export const SuccessResponseGetBookingSchema = z.object({
    success: z.literal(true),
    data: BookingSchema,
});

export type SuccessResponseGetBooking = z.infer<typeof SuccessResponseGetBookingSchema>;

export const ListBookingsDataSchema = z.object({
    bookings: z.array(BookingSchema),
});

export type ListBookingsData = z.infer<typeof ListBookingsDataSchema>;

export const SuccessResponseListBookingsSchema = z.object({
    success: z.literal(true),
    data: ListBookingsDataSchema,
});

export type SuccessResponseListBookings = z.infer<
    typeof SuccessResponseListBookingsSchema
>;

/**
 * Booking field values for a one-off booking (not saved to the catalog).
 * Ignored when `catalogItemId` is provided.
 *
 */
export const CreateBookingDetailsSchema = z.object({
    name: z.string().nullish(),
    description: z.string().nullish(),
    address: z.string().nullish(),
    country: z.string().nullish(),
    website: z.string().nullish(),
    phoneNumber: z.string().nullish(),
    googleUrl: z.string().nullish(),
    category: OfferingCategorySchema.optional(),
    geoPoint: GeoPointSchema.optional(),
});

export type CreateBookingDetails = z.infer<typeof CreateBookingDetailsSchema>;

/**
 * Payload for creating a booking.
 *
 * All booking fields are derived on the server from the request and catalog item.
 * The client only needs to specify which request and optionally which catalog item.
 *
 * Server-derived fields:
 * - `title`, `description`, `address`, etc.: from catalog item (translated)
 * - `geoPoint`: from catalog item or request's Google Place location
 * - `startTime`, `endTime`, `timezone`: from request date/time fields
 * - `numberOfGuests`, `numberOfNights`: from request
 * - `imageURL`: from catalog item's first image
 * - `bookingPolicy`: from catalog item or default for event bookings
 * - `archived`: defaults to false
 *
 */
export const CreateBookingRequestSchema = z.object({
    requestId: z.string(),
    catalogItemId: z.string().nullish(),
    details: CreateBookingDetailsSchema.optional(),
});

export type CreateBookingRequest = z.infer<typeof CreateBookingRequestSchema>;

/**
 * Image associated with a catalog item.
 */
export const CatalogItemImageSchema = z.object({
    id: z.string(),
    url: z.string(),
});

export type CatalogItemImage = z.infer<typeof CatalogItemImageSchema>;

/**
 * A catalog item from search results.
 */
export const CatalogItemSchema = z.object({
    id: z.string(),
    lmtId: z.string(),
    name: z.string(),
    tags: z.array(CatalogItemTagSchema),
    offeringCategories: z.array(OfferingCategorySchema),
    travelItineraryNotes: z.string().nullable(),
    eventDates: z.string().nullable(),
    address: z.string().nullable(),
    addressComponents: z.array(CatalogItemAddressComponentSchema).nullable(),
    locationGeoPoint: GeoPointSchema.nullable(),
    images: z.array(CatalogItemImageSchema),
    published: z.boolean(),
    slug: z.string().nullable(),
    discoverableForTenant: TenantSchema.nullable(),
});

export type CatalogItem = z.infer<typeof CatalogItemSchema>;

export const CatalogItemSearchResultSchema = CatalogItemSchema.and(
    z.object({
        distanceFromLocationInKm: z.number().nullable(),
    }),
);

export type CatalogItemSearchResult = z.infer<typeof CatalogItemSearchResultSchema>;

export const SearchCatalogItemsResponseDataSchema = z.object({
    catalogItems: z.array(CatalogItemSearchResultSchema),
    totalItems: z.int(),
    nextPageCursor: z.string().nullable(),
});

export type SearchCatalogItemsResponseData = z.infer<
    typeof SearchCatalogItemsResponseDataSchema
>;

export const SuccessResponseSearchCatalogItemsSchema = z.object({
    success: z.literal(true),
    data: SearchCatalogItemsResponseDataSchema,
});

export type SuccessResponseSearchCatalogItems = z.infer<
    typeof SuccessResponseSearchCatalogItemsSchema
>;

export const GetThemeCatalogItemsResponseDataSchema = z.object({
    catalogItems: z.array(CatalogItemSchema),
});

export type GetThemeCatalogItemsResponseData = z.infer<
    typeof GetThemeCatalogItemsResponseDataSchema
>;

export const SuccessResponseGetThemeCatalogItemsSchema = z.object({
    success: z.literal(true),
    data: GetThemeCatalogItemsResponseDataSchema,
});

export type SuccessResponseGetThemeCatalogItems = z.infer<
    typeof SuccessResponseGetThemeCatalogItemsSchema
>;

export const CatalogItemFullSchema = CatalogItemSchema.and(
    z.object({
        description: z.string().nullable(),
        notes: z.string().nullable(),
        source: z.string().nullable(),
        googlePlaceId: z.string().nullable(),
        googlePlaceName: z.string().nullish(),
        website: z.string().nullable(),
        phoneNumber: z.string().nullable(),
        googleUrl: z.string().nullable(),
        localInsights: z.string().nullable(),
        headline: z.string().nullable(),
        benefits: z.string().nullable(),
        bookingPolicy: z.string().nullable(),
        albertineVerified: z.iso.datetime().nullable(),
        recentlyOpened: z.iso.datetime().nullable(),
        storyblokArticleId: z.int().nullable(),
        storyblokArticleUuid: z.string().nullable(),
        storyblokArticleSlug: z.string().nullable(),
        shared: z.boolean(),
        archived: z.boolean(),
        createdAt: z.iso.datetime(),
        createdByAgentId: z.string(),
        updatedAt: z.iso.datetime(),
        updatedByAgentId: z.string(),
        sharingLog: z.array(CatalogItemSharingLogEntrySchema),
        supplierLmtIds: z.array(z.string()),
        translations: z.object({
            de: CatalogItemTranslationSchema,
            fr: CatalogItemTranslationSchema,
        }),
    }),
);

export type CatalogItemFull = z.infer<typeof CatalogItemFullSchema>;

export const GetCatalogItemByIdResponseDataSchema = z.object({
    catalogItem: CatalogItemFullSchema,
});

export type GetCatalogItemByIdResponseData = z.infer<
    typeof GetCatalogItemByIdResponseDataSchema
>;

export const SuccessResponseGetCatalogItemByIdSchema = z.object({
    success: z.literal(true),
    data: GetCatalogItemByIdResponseDataSchema,
});

export type SuccessResponseGetCatalogItemById = z.infer<
    typeof SuccessResponseGetCatalogItemByIdSchema
>;

/**
 * Where the catalog item content came from.
 */
export const CatalogItemSourceSchema = z.enum([
    "unknown",
    "insider",
    "proposal",
    "supplier",
    "albertine",
    "other",
]);

export type CatalogItemSource = z.infer<typeof CatalogItemSourceSchema>;

/**
 * A catalog image the agent uploaded and finalized.
 */
export const CatalogItemUploadedImageReferenceSchema = z.object({
    type: z.literal("upload"),
    fileUploadId: z.uuid(),
});

export type CatalogItemUploadedImageReference = z.infer<
    typeof CatalogItemUploadedImageReferenceSchema
>;

/**
 * An image the catalog item already has.
 */
export const CatalogItemExistingImageReferenceSchema = z.object({
    type: z.literal("existing"),
    imageId: z.string().min(1),
});

export type CatalogItemExistingImageReference = z.infer<
    typeof CatalogItemExistingImageReferenceSchema
>;

/**
 * Image to keep or attach when updating a catalog item.
 */
export const CatalogItemImageReferenceSchema = z.union([
    z
        .object({
            type: z.literal("existing"),
        })
        .and(CatalogItemExistingImageReferenceSchema),
    z
        .object({
            type: z.literal("upload"),
        })
        .and(CatalogItemUploadedImageReferenceSchema),
]);

export type CatalogItemImageReference = z.infer<typeof CatalogItemImageReferenceSchema>;

/**
 * Fields an agent can set when creating a catalog item. Name, description,
 * offering categories, and image uploads are required.
 *
 */
export const CreateCatalogItemRequestSchema = z.object({
    name: z.string().min(3).max(255),
    description: z.string().min(3),
    notes: z.string().nullish(),
    source: CatalogItemSourceSchema.nullish(),
    googlePlaceId: z.string().nullish(),
    address: z.string().nullish(),
    addressComponents: z.array(CatalogItemAddressComponentSchema).nullish(),
    website: z.url().nullish(),
    phoneNumber: z.string().nullish(),
    googleUrl: z.url().nullish(),
    locationGeoPoint: GeoPointSchema.nullish(),
    offeringCategories: z.array(OfferingCategorySchema).min(1),
    tags: z.array(CatalogItemTagSchema).optional(),
    supplierLmtIds: z.array(z.string()).optional(),
    localInsights: z.string().nullish(),
    published: z.boolean().optional(),
    headline: z.string().nullish(),
    benefits: z.string().nullish(),
    bookingPolicy: z.string().nullish(),
    albertineVerified: z.iso.datetime().nullish(),
    recentlyOpened: z.iso.datetime().nullish(),
    eventDates: z.string().nullish(),
    travelItineraryNotes: z.string().nullish(),
    googlePlaceData: z.record(z.string(), z.unknown()).nullish(),
    translations: z
        .object({
            de: CatalogItemTranslationSchema.optional(),
            fr: CatalogItemTranslationSchema.optional(),
        })
        .optional(),
    imageFileUploadIds: z.array(z.uuid()).min(1),
    discoverableForTenant: TenantSchema.nullish(),
});

export type CreateCatalogItemRequest = z.infer<typeof CreateCatalogItemRequestSchema>;

/**
 * Partial update of the fields an agent can edit. Omitted fields are left
 * unchanged. When `images` is present it replaces the current images.
 *
 */
export const UpdateCatalogItemRequestSchema = z.object({
    name: z.string().min(3).max(255).optional(),
    description: z.string().min(3).nullish(),
    notes: z.string().nullish(),
    source: CatalogItemSourceSchema.nullish(),
    googlePlaceId: z.string().nullish(),
    address: z.string().nullish(),
    addressComponents: z.array(CatalogItemAddressComponentSchema).nullish(),
    website: z.url().nullish(),
    phoneNumber: z.string().nullish(),
    googleUrl: z.url().nullish(),
    locationGeoPoint: GeoPointSchema.nullish(),
    offeringCategories: z.array(OfferingCategorySchema).min(1).optional(),
    tags: z.array(CatalogItemTagSchema).optional(),
    supplierLmtIds: z.array(z.string()).optional(),
    localInsights: z.string().nullish(),
    published: z.boolean().optional(),
    headline: z.string().nullish(),
    benefits: z.string().nullish(),
    bookingPolicy: z.string().nullish(),
    albertineVerified: z.iso.datetime().nullish(),
    recentlyOpened: z.iso.datetime().nullish(),
    eventDates: z.string().nullish(),
    travelItineraryNotes: z.string().nullish(),
    googlePlaceData: z.record(z.string(), z.unknown()).nullish(),
    translations: z
        .object({
            de: CatalogItemTranslationSchema.optional(),
            fr: CatalogItemTranslationSchema.optional(),
        })
        .optional(),
    images: z.array(CatalogItemImageReferenceSchema).optional(),
    discoverableForTenant: TenantSchema.nullish(),
});

export type UpdateCatalogItemRequest = z.infer<typeof UpdateCatalogItemRequestSchema>;

export const SuccessResponseCatalogItemFullSchema = z.object({
    success: z.literal(true),
    data: CatalogItemFullSchema,
});

export type SuccessResponseCatalogItemFull = z.infer<
    typeof SuccessResponseCatalogItemFullSchema
>;

export const CatalogImageFileUploadPurposeSchema = z.object({
    type: z.literal("catalog-image"),
});

export type CatalogImageFileUploadPurpose = z.infer<
    typeof CatalogImageFileUploadPurposeSchema
>;

export const ChatMessageAttachmentFileUploadPurposeSchema = z.object({
    type: z.literal("chat-message-attachment"),
    memberId: z.string(),
});

export type ChatMessageAttachmentFileUploadPurpose = z.infer<
    typeof ChatMessageAttachmentFileUploadPurposeSchema
>;

export const FileUploadPurposeSchema = z.union([
    z
        .object({
            type: z.literal("catalog-image"),
        })
        .and(CatalogImageFileUploadPurposeSchema),
    z
        .object({
            type: z.literal("chat-message-attachment"),
        })
        .and(ChatMessageAttachmentFileUploadPurposeSchema),
]);

export type FileUploadPurpose = z.infer<typeof FileUploadPurposeSchema>;

/**
 * Where the file bytes come from. `client` means the caller sends them;
 * `url` means the server downloads them from a URL.
 *
 */
export const FileUploadSourceSchema = z.enum(["client", "url"]);

export type FileUploadSource = z.infer<typeof FileUploadSourceSchema>;

/**
 * Upload a file the caller sends.
 */
export const InitiateClientFileUploadRequestSchema = z.object({
    source: z.literal("client"),
    uploadPurpose: FileUploadPurposeSchema,
    mimeType: z.string().min(1),
    fileName: z.string().min(1),
});

export type InitiateClientFileUploadRequest = z.infer<
    typeof InitiateClientFileUploadRequestSchema
>;

/**
 * Import a file the server downloads from a URL.
 */
export const InitiateUrlFileUploadRequestSchema = z.object({
    source: z.literal("url"),
    uploadPurpose: FileUploadPurposeSchema,
    url: z.url(),
});

export type InitiateUrlFileUploadRequest = z.infer<
    typeof InitiateUrlFileUploadRequestSchema
>;

export const InitiateFileUploadRequestSchema = z.union([
    z
        .object({
            source: z.literal("client"),
        })
        .and(InitiateClientFileUploadRequestSchema),
    z
        .object({
            source: z.literal("url"),
        })
        .and(InitiateUrlFileUploadRequestSchema),
]);

export type InitiateFileUploadRequest = z.infer<typeof InitiateFileUploadRequestSchema>;

export const InitiateClientFileUploadDataSchema = z.object({
    source: z.literal("client"),
    id: z.uuid(),
    uploadUrl: z.url(),
    uploadHeaders: z.record(z.string(), z.string()),
    uploadHttpMethod: z.enum(["put", "post"]),
});

export type InitiateClientFileUploadData = z.infer<
    typeof InitiateClientFileUploadDataSchema
>;

export const InitiateUrlFileUploadDataSchema = z.object({
    source: z.literal("url"),
    id: z.uuid(),
});

export type InitiateUrlFileUploadData = z.infer<typeof InitiateUrlFileUploadDataSchema>;

export const InitiateFileUploadDataSchema = z.union([
    z
        .object({
            source: z.literal("client"),
        })
        .and(InitiateClientFileUploadDataSchema),
    z
        .object({
            source: z.literal("url"),
        })
        .and(InitiateUrlFileUploadDataSchema),
]);

export type InitiateFileUploadData = z.infer<typeof InitiateFileUploadDataSchema>;

export const SuccessResponseInitiateFileUploadSchema = z.object({
    success: z.literal(true),
    data: InitiateFileUploadDataSchema,
});

export type SuccessResponseInitiateFileUpload = z.infer<
    typeof SuccessResponseInitiateFileUploadSchema
>;

export const FinalizeFileUploadPathParamsSchema = z.object({
    id: z.uuid(),
});

export type FinalizeFileUploadPathParams = z.infer<
    typeof FinalizeFileUploadPathParamsSchema
>;

export const FinalizeFileUploadDataSchema = z.object({
    status: z.enum(["finalized", "rejected"]),
});

export type FinalizeFileUploadData = z.infer<typeof FinalizeFileUploadDataSchema>;

export const SuccessResponseFinalizeFileUploadSchema = z.object({
    success: z.literal(true),
    data: FinalizeFileUploadDataSchema,
});

export type SuccessResponseFinalizeFileUpload = z.infer<
    typeof SuccessResponseFinalizeFileUploadSchema
>;

export const GetFileUploadPathParamsSchema = z.object({
    id: z.uuid(),
});

export type GetFileUploadPathParams = z.infer<typeof GetFileUploadPathParamsSchema>;

/**
 * Why an upload was rejected. `url-not-allowed` and `download-failed` only
 * apply to `source: url`.
 *
 */
export const FileUploadRejectionReasonSchema = z.enum([
    "invalid-file-type",
    "invalid-mime-type",
    "upload-timed-out",
    "url-not-allowed",
    "download-failed",
]);

export type FileUploadRejectionReason = z.infer<typeof FileUploadRejectionReasonSchema>;

export const FileUploadStatusDataSchema = z.object({
    id: z.uuid(),
    source: FileUploadSourceSchema,
    status: z.enum(["pending", "finalized", "rejected"]),
    rejectionReason: FileUploadRejectionReasonSchema.nullable(),
});

export type FileUploadStatusData = z.infer<typeof FileUploadStatusDataSchema>;

export const SuccessResponseGetFileUploadSchema = z.object({
    success: z.literal(true),
    data: FileUploadStatusDataSchema,
});

export type SuccessResponseGetFileUpload = z.infer<
    typeof SuccessResponseGetFileUploadSchema
>;

/**
 * Payload for creating a request.
 * Server sets `createdBy`, `status` (draft), `archived` (false), `title` (empty),
 * `assignedToName` (resolved from agent ID), rolling `ticketNumber`, and `source`.
 *
 */
export const CreateRequestRequestSchema = z.object({
    memberId: z.string(),
    assignedToAgentId: z.string().nullish(),
    createdFromMessageIds: z.array(z.string()).optional(),
});

export type CreateRequestRequest = z.infer<typeof CreateRequestRequestSchema>;

export const CreateRequestDataSchema = z.object({
    id: z.string(),
});

export type CreateRequestData = z.infer<typeof CreateRequestDataSchema>;

export const SuccessResponseCreateRequestSchema = z.object({
    success: z.literal(true),
    data: CreateRequestDataSchema,
});

export type SuccessResponseCreateRequest = z.infer<
    typeof SuccessResponseCreateRequestSchema
>;

export const RequestStatusSchema = z.enum([
    "new",
    "draft",
    "open",
    "confirmed",
    "closed",
]);

export type RequestStatus = z.infer<typeof RequestStatusSchema>;

export const RequestOutcomeSchema = z.enum([
    "notBooked",
    "booked",
    "noBookingNeeded",
    "memberCancelled",
    "noActionNeeded",
]);

export type RequestOutcome = z.infer<typeof RequestOutcomeSchema>;

export const RequestBookingOutcomeSchema = z.enum([
    "firstChoiceBooked",
    "alternativeChoiceBooked",
]);

export type RequestBookingOutcome = z.infer<typeof RequestBookingOutcomeSchema>;

export const RequestDeadlineStepSchema = z.enum([
    "revisit_request",
    "send_status_update",
    "send_status_update_internal",
    "send_proposal",
    "send_booking_confirmation",
    "wait_for_member_response",
    "wait_for_supplier_response",
    "check_on_pending_payment",
    "no_further_action",
]);

export type RequestDeadlineStep = z.infer<typeof RequestDeadlineStepSchema>;

/**
 * Request detail from PostgreSQL.
 */
export const RequestSchema = z.object({
    id: z.string(),
    memberId: z.string(),
    conversationId: z.string(),
    status: RequestStatusSchema,
    ticketNumber: z.int(),
    archived: z.boolean(),
    title: z.string().nullable(),
    startDate: z.string().nullable(),
    startTime: z.string().nullable(),
    endDate: z.string().nullable(),
    endTime: z.string().nullable(),
    offeringCategory: z.string().nullable(),
    numberOfGuests: z.int().nullable(),
    createdAt: z.iso.datetime(),
    assignedToAgentId: z.string().nullable(),
    createdFromMessageId: z.string().nullable(),
    firstChoice: z.string().nullable(),
    notes: z.string().nullable(),
    budget: z.string().nullable(),
    outcome: RequestOutcomeSchema.nullable(),
    bookingOutcome: RequestBookingOutcomeSchema.nullable(),
    source: RequestSourceSchema.nullable(),
    sourceChannel: MemberPreferredCommunicationChannelSchema.nullable(),
    nextDeadlineStep: RequestDeadlineStepSchema.nullable(),
    nextDeadlineAt: z.iso.datetime().nullable(),
    deadlineCreatedAt: z.iso.datetime().nullable(),
    locationGooglePlaceId: z.string().nullable(),
    locationGoogleName: z.string().nullable(),
    locationGoogleFormattedAddress: z.string().nullable(),
    locationGoogleAddressComponents: z
        .array(CatalogItemAddressComponentSchema)
        .nullable(),
    locationGoogleGeometryLat: z.number().nullable(),
    locationGoogleGeometryLng: z.number().nullable(),
    highlightedFields: z.array(RequestHighlightedFieldSchema).nullable(),
    aiCaptureStatus: RequestAiCaptureStatusSchema.nullable(),
    memberRating: z.int().nullable(),
    memberFeedback: z.string().nullable(),
    memberFeedbackTimestamp: z.iso.datetime().nullable(),
    proposalBookings: z.array(z.string()).nullable(),
    proposal: RequestProposalSchema.nullable(),
});

export type Request = z.infer<typeof RequestSchema>;

export const SuccessResponseGetRequestSchema = z.object({
    success: z.literal(true),
    data: RequestSchema,
});

export type SuccessResponseGetRequest = z.infer<typeof SuccessResponseGetRequestSchema>;

/**
 * Offering category filter value, including uncategorized requests.
 */
export const RequestOfferingCategoryFilterSchema = z.enum([
    "dining",
    "nightlife",
    "flight",
    "train",
    "accommodation",
    "travelItinerary",
    "transportation",
    "event",
    "localExperience",
    "shopping",
    "wellness",
    "services",
    "other",
    "uncategorized",
]);

export type RequestOfferingCategoryFilter = z.infer<
    typeof RequestOfferingCategoryFilterSchema
>;

export const ListRequestsSortFieldSchema = z.enum([
    "nextDeadlineAt",
    "ticketNumber",
    "startDateTime",
]);

export type ListRequestsSortField = z.infer<typeof ListRequestsSortFieldSchema>;

/**
 * Query parameters for GET /requests.
 */
export const ListRequestsQuerySchema = z.object({
    page: z.coerce.number().int().gte(0).optional().default(0),
    size: z.coerce.number().int().gte(1).lte(1000).optional().default(100),
    memberId: z.string().optional(),
    searchTerm: z.string().optional(),
    status: z
        .preprocess((val) => {
            if (!val) return undefined;
            return Array.isArray(val) ? val : [String(val)];
        }, z.array(RequestStatusSchema))
        .optional(),
    offeringCategory: z
        .preprocess((val) => {
            if (!val) return undefined;
            return Array.isArray(val) ? val : [String(val)];
        }, z.array(RequestOfferingCategoryFilterSchema))
        .optional(),
    nextDeadlineStep: z
        .preprocess((val) => {
            if (!val) return undefined;
            return Array.isArray(val) ? val : [String(val)];
        }, z.array(RequestDeadlineStepSchema))
        .optional(),
    outcome: z
        .preprocess((val) => {
            if (!val) return undefined;
            return Array.isArray(val) ? val : [String(val)];
        }, z.array(RequestOutcomeSchema))
        .optional(),
    assignedTo: z
        .preprocess((val) => {
            if (!val) return undefined;
            return Array.isArray(val) ? val : [String(val)];
        }, z.array(z.string()))
        .optional(),
    teamIds: z
        .preprocess((val) => {
            if (!val) return undefined;
            return Array.isArray(val) ? val : [String(val)];
        }, z.array(z.string()))
        .optional(),
    bookingOutcome: z
        .preprocess((val) => {
            if (!val) return undefined;
            return Array.isArray(val) ? val : [String(val)];
        }, z.array(RequestBookingOutcomeSchema))
        .optional(),
    memberMembershipTier: z
        .preprocess((val) => {
            if (!val) return undefined;
            return Array.isArray(val) ? val : [String(val)];
        }, z.array(z.string()))
        .optional(),
    sortBy: ListRequestsSortFieldSchema.optional(),
    sortOrder: z.enum(["asc", "desc"]).optional(),
});

export type ListRequestsQuery = z.infer<typeof ListRequestsQuerySchema>;

/**
 * Request row for the LMT requests list.
 */
export const ListRequestsItemSchema = z.object({
    id: z.string(),
    archived: z.boolean().optional(),
    assignedTo: z.string().nullish(),
    assignedToName: z.string().nullish(),
    title: z.string().nullish(),
    status: RequestStatusSchema,
    startDate: z.string().nullish(),
    startTime: z.string().nullish(),
    endDate: z.string().nullish(),
    endTime: z.string().nullish(),
    offeringCategory: z.string().nullish(),
    outcome: RequestOutcomeSchema.nullish(),
    bookingOutcome: RequestBookingOutcomeSchema.nullish(),
    ticketNumberText: z.string(),
    conversation: z.string(),
    member: z.string(),
    memberName: z.string(),
    memberMembershipTier: z.string().nullish(),
    tenant: z.string(),
    memberAssignedTo: z.string().nullable(),
    nextDeadlineStep: RequestDeadlineStepSchema.nullable(),
    nextDeadlineAt: z.iso.datetime().nullish(),
    startDateTime: z.iso.datetime().nullish(),
    endDateTime: z.iso.datetime().nullish(),
    createdAt: z.iso.datetime(),
    memberFeedbackTimestamp: z.iso.datetime().nullish(),
    locationGoogleFormattedAddress: z.string().nullable(),
    ticketNumber: z.int(),
    numberOfGuests: z.int().nullish(),
    memberRating: z.int().nullish(),
});

export type ListRequestsItem = z.infer<typeof ListRequestsItemSchema>;

export const ListRequestsPaginationSchema = z.object({
    total: z.int(),
    page: z.int(),
    size: z.int(),
});

export type ListRequestsPagination = z.infer<typeof ListRequestsPaginationSchema>;

export const ListRequestsDataSchema = z.object({
    requests: z.array(ListRequestsItemSchema),
    pagination: ListRequestsPaginationSchema,
});

export type ListRequestsData = z.infer<typeof ListRequestsDataSchema>;

export const SuccessResponseListRequestsSchema = z.object({
    success: z.literal(true),
    data: ListRequestsDataSchema,
});

export type SuccessResponseListRequests = z.infer<
    typeof SuccessResponseListRequestsSchema
>;

export const MentionStatusSchema = z.enum(["open", "resolved"]);

export type MentionStatus = z.infer<typeof MentionStatusSchema>;

export const ListTasksSortFieldSchema = z.enum([
    "mentionCreatedAt",
    "ticketNumber",
    "mentionDueDate",
]);

export type ListTasksSortField = z.infer<typeof ListTasksSortFieldSchema>;

/**
 * Partial update payload for agent-editable request fields (title, category, notes,
 * dates, location, assignment, etc.). Server resolves `assignedToName` from
 * `assignedTo`. Setting `offeringCategory` to
 * `null` deletes the category on the request without cascading to bookings.
 * Each field key present in the payload is removed from the request's
 * `highlightedFields` (AI-capture highlights), leaving the other highlighted
 * fields untouched. Status, outcome, booking outcome, and deadline changes must use
 * `changeRequestStatus`. Use `archiveRequest` / `unarchiveRequest` to change archive
 * state.
 *
 */
export const UpdateRequestRequestSchema = z.object({
    assignedTo: z.string().nullish(),
    title: z.string().nullish(),
    offeringCategory: OfferingCategorySchema.nullish(),
    firstChoice: z.string().nullish(),
    notes: z.string().nullish(),
    numberOfGuests: z.int().nullish(),
    source: z.union([z.literal("reactive"), z.literal("proactive")]).nullish(),
    sourceChannel: MemberPreferredCommunicationChannelSchema.nullish(),
    budget: z.string().nullish(),
    startDate: z.string().nullish(),
    startTime: z.string().nullish(),
    endDate: z.string().nullish(),
    endTime: z.string().nullish(),
    locationGooglePlaceId: z.string().nullish(),
    locationGoogleName: z.string().nullish(),
    locationGoogleFormattedAddress: z.string().nullish(),
    locationGoogleAddressComponents: z
        .array(CatalogItemAddressComponentSchema)
        .nullish(),
    locationGoogleGeometryLat: z.number().nullish(),
    locationGoogleGeometryLng: z.number().nullish(),
});

export type UpdateRequestRequest = z.infer<typeof UpdateRequestRequestSchema>;

/**
 * Offering categories that require a booking resolution (first choice vs alternative)
 * when confirming a request via `changeRequestStatus`.
 *
 */
export const BookingResolutionOfferingCategorySchema = z.enum([
    "accommodation",
    "dining",
    "flight",
    "nightlife",
    "train",
    "transportation",
    "travelItinerary",
]);

export type BookingResolutionOfferingCategory = z.infer<
    typeof BookingResolutionOfferingCategorySchema
>;

/**
 * Terminal status-machine action. The server derives `status`, outcomes, and deadline
 * fields. `intent` is required for `set_next_step`.
 *
 */
export const ChangeRequestStatusRequestSchema = z.object({
    action: z.enum([
        "set_next_step",
        "confirm_no_further_action",
        "close",
        "change_booking_resolution",
        "change_closed_resolution",
    ]),
    intent: z.enum(["update", "confirm", "reopen"]).optional(),
    nextDeadlineStep: RequestDeadlineStepSchema.optional(),
    nextDeadlineAt: z.iso.datetime().optional(),
    outcome: RequestOutcomeSchema.nullish(),
    bookingOutcome: RequestBookingOutcomeSchema.nullish(),
});

export type ChangeRequestStatusRequest = z.infer<
    typeof ChangeRequestStatusRequestSchema
>;

/**
 * Query parameters for GET /tasks.
 */
export const ListTasksQuerySchema = z.object({
    page: z.coerce.number().int().gte(0).optional().default(0),
    size: z.coerce.number().int().gte(1).lte(1000).optional().default(100),
    searchTerm: z.string().optional(),
    mentionStatus: z
        .preprocess((val) => {
            if (!val) return undefined;
            return Array.isArray(val) ? val : [String(val)];
        }, z.array(MentionStatusSchema))
        .optional(),
    assignedToAgentIds: z
        .preprocess((val) => {
            if (!val) return undefined;
            return Array.isArray(val) ? val : [String(val)];
        }, z.array(z.string()))
        .optional(),
    sortBy: ListTasksSortFieldSchema.optional().default("mentionCreatedAt"),
    sortOrder: z.enum(["asc", "desc"]).optional(),
});

export type ListTasksQuery = z.infer<typeof ListTasksQuerySchema>;

/**
 * Task row for the LMT tasks list (internal note with a mention).
 */
export const ListTasksItemSchema = z.object({
    id: z.string(),
    memberId: z.string(),
    requestId: z.string().nullable(),
    mentionCreatedBy: z.string(),
    mentionCreatedAt: z.iso.datetime(),
    mentionDueDate: z.iso.datetime().nullable(),
    mentionResolvedBy: z.string().nullable(),
    mentionResolvedAt: z.iso.datetime().nullable(),
    mentionedAgentIds: z.array(z.string()),
    mentionStatus: MentionStatusSchema,
    requestTicketNumber: z.int().nullable(),
    message: z.string(),
    memberName: z.string(),
    requestTitle: z.string().nullable(),
});

export type ListTasksItem = z.infer<typeof ListTasksItemSchema>;

export const ListTasksPaginationSchema = z.object({
    total: z.int(),
    page: z.int(),
    size: z.int(),
});

export type ListTasksPagination = z.infer<typeof ListTasksPaginationSchema>;

export const ListTasksDataSchema = z.object({
    tasks: z.array(ListTasksItemSchema),
    pagination: ListTasksPaginationSchema,
});

export type ListTasksData = z.infer<typeof ListTasksDataSchema>;

export const SuccessResponseListTasksSchema = z.object({
    success: z.literal(true),
    data: ListTasksDataSchema,
});

export type SuccessResponseListTasks = z.infer<typeof SuccessResponseListTasksSchema>;

export const ListIncomingMessagesQuerySchema = z.object({
    after: z.iso.datetime(),
});

export type ListIncomingMessagesQuery = z.infer<typeof ListIncomingMessagesQuerySchema>;

export const IncomingMessageAttachmentSchema = z.object({
    mimeType: z.string().nullable(),
    fileName: z.string().nullable(),
});

export type IncomingMessageAttachment = z.infer<typeof IncomingMessageAttachmentSchema>;

export const IncomingMessageSchema = z.object({
    sentAt: z.iso.datetime(),
    senderName: z.string(),
    text: z.string(),
    hasQuickRequest: z.boolean(),
    attachments: z.array(IncomingMessageAttachmentSchema),
    memberId: z.string(),
    requestId: z.string().nullable(),
    requestTitle: z.string().nullable(),
});

export type IncomingMessage = z.infer<typeof IncomingMessageSchema>;

export const ListIncomingMessagesDataSchema = z.object({
    items: z.array(IncomingMessageSchema),
});

export type ListIncomingMessagesData = z.infer<typeof ListIncomingMessagesDataSchema>;

export const SuccessResponseListIncomingMessagesSchema = z.object({
    success: z.literal(true),
    data: ListIncomingMessagesDataSchema,
});

export type SuccessResponseListIncomingMessages = z.infer<
    typeof SuccessResponseListIncomingMessagesSchema
>;

export const ListRecentConversationsQuerySchema = z.object({
    assignedToAgentIds: z
        .preprocess((val) => {
            if (!val) return undefined;
            return Array.isArray(val) ? val : [String(val)];
        }, z.array(z.string()))
        .optional(),
    teamIds: z
        .preprocess((val) => {
            if (!val) return undefined;
            return Array.isArray(val) ? val : [String(val)];
        }, z.array(z.string()))
        .optional(),
});

export type ListRecentConversationsQuery = z.infer<
    typeof ListRecentConversationsQuerySchema
>;

export const RecentConversationMessageAttachmentSchema = z.object({
    mimeType: z.string().nullable(),
    fileName: z.string().nullable(),
});

export type RecentConversationMessageAttachment = z.infer<
    typeof RecentConversationMessageAttachmentSchema
>;

export const RecentConversationMessageSchema = z.object({
    sentBy: z.string(),
    senderName: z.string(),
    sentAt: z.iso.datetime(),
    message: z.string(),
    requestId: z.string().nullable(),
    memberReaction: z.string().nullable(),
    attachments: z.array(RecentConversationMessageAttachmentSchema),
});

export type RecentConversationMessage = z.infer<typeof RecentConversationMessageSchema>;

export const RecentConversationSchema = z.object({
    id: z.string(),
    unreadMessagesCountByAgent: z.int(),
    agentUnseenReactionsCount: z.int(),
    memberLastReactionTimestamp: z.iso.datetime().nullable(),
    agentLastUnseenReactionMessageId: z.string().nullable(),
    lastMessage: RecentConversationMessageSchema.nullable(),
    lastMessageMemberReactedTo: RecentConversationMessageSchema.nullable(),
});

export type RecentConversation = z.infer<typeof RecentConversationSchema>;

export const RecentConversationMemberSchema = z.object({
    id: z.string(),
    title: z.string().nullable(),
    firstName: z.string(),
    lastName: z.string(),
    assignedToAgentId: z.string().nullable(),
    assignedToAgentName: z.string().nullable(),
    isTestMember: z.boolean(),
    isVipMember: z.boolean(),
});

export type RecentConversationMember = z.infer<typeof RecentConversationMemberSchema>;

export const RecentConversationRequestSchema = z.object({
    id: z.string(),
    title: z.string().nullable(),
    status: RequestStatusSchema,
    ticketNumber: z.int(),
});

export type RecentConversationRequest = z.infer<typeof RecentConversationRequestSchema>;

export const RecentConversationsItemSchema = z.object({
    conversation: RecentConversationSchema,
    member: RecentConversationMemberSchema,
    requests: z.array(RecentConversationRequestSchema),
});

export type RecentConversationsItem = z.infer<typeof RecentConversationsItemSchema>;

export const ListRecentConversationsDataSchema = z.object({
    items: z.array(RecentConversationsItemSchema),
});

export type ListRecentConversationsData = z.infer<
    typeof ListRecentConversationsDataSchema
>;

export const SuccessResponseListRecentConversationsSchema = z.object({
    success: z.literal(true),
    data: ListRecentConversationsDataSchema,
});

export type SuccessResponseListRecentConversations = z.infer<
    typeof SuccessResponseListRecentConversationsSchema
>;

export const DueTasksDataSchema = z.object({
    dueNow: z.array(ListTasksItemSchema),
    dueInExactly30Minutes: z.array(ListTasksItemSchema),
    dueInExactly60Minutes: z.array(ListTasksItemSchema),
});

export type DueTasksData = z.infer<typeof DueTasksDataSchema>;

export const SuccessResponseDueTasksSchema = z.object({
    success: z.literal(true),
    data: DueTasksDataSchema,
});

export type SuccessResponseDueTasks = z.infer<typeof SuccessResponseDueTasksSchema>;

export const DueRequestsCountDataSchema = z.object({
    overdue: z.int(),
    dueInTheNextHour: z.int(),
});

export type DueRequestsCountData = z.infer<typeof DueRequestsCountDataSchema>;

export const SuccessResponseDueRequestsCountSchema = z.object({
    success: z.literal(true),
    data: DueRequestsCountDataSchema,
});

export type SuccessResponseDueRequestsCount = z.infer<
    typeof SuccessResponseDueRequestsCountSchema
>;

/**
 * Compact request shape for due-request reminders.
 */
export const DueRequestSummarySchema = z.object({
    id: z.string(),
    title: z.string().nullish(),
    status: RequestStatusSchema,
    ticketNumber: z.int(),
    assignedToAgentId: z.string().nullish(),
    nextDeadlineStep: RequestDeadlineStepSchema.nullable(),
    nextDeadlineAt: z.iso.datetime().nullable(),
});

export type DueRequestSummary = z.infer<typeof DueRequestSummarySchema>;

export const DueRequestsDataSchema = z.object({
    dueNow: z.array(DueRequestSummarySchema),
    dueInExactly30Minutes: z.array(DueRequestSummarySchema),
    dueInExactly60Minutes: z.array(DueRequestSummarySchema),
});

export type DueRequestsData = z.infer<typeof DueRequestsDataSchema>;

export const SuccessResponseDueRequestsSchema = z.object({
    success: z.literal(true),
    data: DueRequestsDataSchema,
});

export type SuccessResponseDueRequests = z.infer<
    typeof SuccessResponseDueRequestsSchema
>;

/**
 * AI-generated explanation for why a catalog item was recommended.
 */
export const RecommendationRationaleSchema = z.object({
    whyRelevant: z.string().nullable(),
    potentialRisks: z.string().nullable(),
    bestUseCase: z.string().nullable(),
});

export type RecommendationRationale = z.infer<typeof RecommendationRationaleSchema>;

/**
 * Fields shared by every ranked recommendation, regardless of source.
 */
export const RecommendationItemBaseSchema = z.object({
    source: z.enum(["catalog", "web"]),
    id: z.string(),
    lmtId: z.string().nullable(),
    name: z.string(),
    description: z.string().nullable(),
    score: z.number().gte(1).lte(10),
    rationale: RecommendationRationaleSchema,
    address: z.string().nullable(),
    city: z.string().nullable(),
    country: z.string().nullable(),
    googleUrl: z.string().nullable(),
});

export type RecommendationItemBase = z.infer<typeof RecommendationItemBaseSchema>;

/**
 * A recommendation backed by an internal catalog item. Rich place data is resolved from the catalog item itself, so no placeId/category is carried here.
 */
export const CatalogRecommendationItemSchema = RecommendationItemBaseSchema.and(
    z.object({
        source: z.enum(["catalog"]),
    }),
);

export type CatalogRecommendationItem = z.infer<typeof CatalogRecommendationItemSchema>;

/**
 * A recommendation sourced from web grounding, carrying the Google Place ID and offering category resolved during external search.
 */
export const WebRecommendationItemSchema = RecommendationItemBaseSchema.and(
    z.object({
        source: z.enum(["web"]),
        placeId: z.string().nullable(),
        category: OfferingCategorySchema,
    }),
);

export type WebRecommendationItem = z.infer<typeof WebRecommendationItemSchema>;

/**
 * A ranked recommendation for a request, from the internal catalog or the web. Discriminated by `source`.
 */
export const RecommendationItemSchema = z.union([
    z
        .object({
            source: z.literal("catalog"),
        })
        .and(CatalogRecommendationItemSchema),
    z
        .object({
            source: z.literal("web"),
        })
        .and(WebRecommendationItemSchema),
]);

export type RecommendationItem = z.infer<typeof RecommendationItemSchema>;

/**
 * A web page consulted during external search (Google grounding citation). Not mapped to a specific item.
 */
export const RecommendationSourceSchema = z.object({
    title: z.string().nullable(),
    url: z.string(),
});

export type RecommendationSource = z.infer<typeof RecommendationSourceSchema>;

export const PublicProposalAttachmentSchema = z.object({
    name: z.string(),
    url: z.string().optional(),
});

export type PublicProposalAttachment = z.infer<typeof PublicProposalAttachmentSchema>;

export const PublicProposalPriceSchema = z.object({
    amount: z.number().gte(0),
    currency: z.string().length(3),
});

export type PublicProposalPrice = z.infer<typeof PublicProposalPriceSchema>;

export const PublicProposalBookingOptionSchema = z.object({
    id: z.string(),
    title: z.string().optional(),
    description: z.string().optional(),
    imageURLs: z.array(z.string()).optional(),
    confirmed: z.boolean().optional(),
    price: PublicProposalPriceSchema.optional(),
});

export type PublicProposalBookingOption = z.infer<
    typeof PublicProposalBookingOptionSchema
>;

export const PublicProposalBookingSchema = z.object({
    id: z.string(),
    template: z.enum(["accommodation", "general"]),
    offeringCategory: OfferingCategorySchema.optional(),
    title: z.string(),
    description: z.string(),
    headline: z.string().optional(),
    notesForMember: z.string().optional(),
    notesForMemberSenderName: z.string().optional(),
    benefits: z.string().optional(),
    images: z.array(z.string()),
    attachments: z.array(PublicProposalAttachmentSchema),
    website: z.string().optional(),
    phoneNumber: z.string().optional(),
    address: z.string().optional(),
    mapUrl: z.string().optional(),
    confirmed: z.boolean(),
    bookingPolicy: z.string().optional(),
    rooms: z.string().optional(),
    price: PublicProposalPriceSchema.optional(),
    priceDetails: z.string().optional(),
    startTime: z.string().optional(),
    startTimezone: z.string().optional(),
    endTime: z.string().optional(),
    endTimezone: z.string().optional(),
    numberOfGuests: z.int().optional(),
    numberOfNights: z.int().optional(),
    options: z.array(PublicProposalBookingOptionSchema).optional(),
});

export type PublicProposalBooking = z.infer<typeof PublicProposalBookingSchema>;

export const RequestProposalResponseDataSchema = z.object({
    id: z.string(),
    title: z.string(),
    startDate: z.string().optional(),
    startTime: z.string().optional(),
    endDate: z.string().optional(),
    endTime: z.string().optional(),
    numberOfGuests: z.int().optional(),
    bookings: z.array(PublicProposalBookingSchema),
});

export type RequestProposalResponseData = z.infer<
    typeof RequestProposalResponseDataSchema
>;

export const SuccessResponseRequestProposalSchema = z.object({
    success: z.literal(true),
    data: RequestProposalResponseDataSchema,
});

export type SuccessResponseRequestProposal = z.infer<
    typeof SuccessResponseRequestProposalSchema
>;

export const RequestRecommendationsResponseDataSchema = z.object({
    items: z.array(RecommendationItemSchema),
    sources: z.array(RecommendationSourceSchema),
});

export type RequestRecommendationsResponseData = z.infer<
    typeof RequestRecommendationsResponseDataSchema
>;

export const SuccessResponseRequestRecommendationsSchema = z.object({
    success: z.literal(true),
    data: RequestRecommendationsResponseDataSchema,
});

export type SuccessResponseRequestRecommendations = z.infer<
    typeof SuccessResponseRequestRecommendationsSchema
>;

export const PreviewMemberDiscoverThemesPathParamsSchema = z.object({
    memberId: z.string(),
});

export type PreviewMemberDiscoverThemesPathParams = z.infer<
    typeof PreviewMemberDiscoverThemesPathParamsSchema
>;

/**
 * Content themes personalized for a member.
 */
export const PreviewMemberDiscoverThemesResponseDataSchema = z.object({
    spotlightTheme: ThemeSchema.nullable(),
    cityGuideThemes: z.array(ThemeSchema),
    personalisedThemes: z.array(ThemeSchema),
});

export type PreviewMemberDiscoverThemesResponseData = z.infer<
    typeof PreviewMemberDiscoverThemesResponseDataSchema
>;

export const SuccessResponsePreviewMemberDiscoverThemesSchema = z.object({
    success: z.literal(true),
    data: PreviewMemberDiscoverThemesResponseDataSchema,
});

export type SuccessResponsePreviewMemberDiscoverThemes = z.infer<
    typeof SuccessResponsePreviewMemberDiscoverThemesSchema
>;
